import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ProductionDepartmentSwitcher from './ProductionDepartmentSwitcher';
import ConfirmDialog from './ConfirmDialog';
import JoinDepartmentDialog, { UNSAVED_SWITCH_TITLE, UNSAVED_SWITCH_MESSAGE, toBody } from './JoinDepartmentDialog';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, apiGet } from '../utils/api';
import { ROLE_LABELS, APPROVAL_ROLES, DEPARTMENT_ROLES } from '../constants/statuses';
import { spaceLabelFor } from '../utils/spaceLabel';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useToast } from '../contexts/ToastContext';
import usePageViewLogger from '../hooks/usePageViewLogger';
import { getMountedNavigationBlockerCount } from '../hooks/useUnsavedChangesGuard';
import { subscribe as subscribeDataBus } from '../utils/dataBus';
import { worstUrgencyColour } from '../utils/urgencyColour';
import { BTN_XS } from '../constants/buttons';
import useMeasuredHeight from '../hooks/useMeasuredHeight';
import usePollingRefresh, { POLL_INTERVAL_MS } from '../hooks/usePollingRefresh';
import { notificationTarget } from '../utils/notificationTarget';

// Per-user-scoped localStorage key for sidebar section collapse state.
// Keeps each individual's expand/collapse choice separate so impersonating
// a different user doesn't inherit the previous user's preference.
const userScopedKey = (base) => {
  const userId = localStorage.getItem('sfx_user_id') || '';
  return userId ? `${base}_${userId}` : base;
};

// THE tooltip on the My Orders / Dept Orders urgency dot — FOUR call sites
// (sidebar + mobile, My Orders + Dept Orders), extracted so the wording can
// only drift once (Code Unification Principle).
//
// 🔀 REWORDED 2026-08-18. Owner ruling: once an order is PLACED (`ordered` /
// `partially_delivered`), the server's `classifyOrders`
// (server/routes/notifications.js) stops counting it toward this badge —
// its row already shows a green "Order placed" dot and no countdown chrome
// (`isPlacedStatus`, client/src/constants/statuses.js). The old wording
// ("N orders with tight lead times") went unchanged, so a reader comparing
// the badge to a table full of green dots had no way to tell the two
// numbers were answering different questions. The new wording says what the
// count now excludes.
function tightLeadTimesTooltip(count) {
  return `${count} order${count !== 1 ? 's' : ''} not yet placed, with tight lead times`;
}

// SVG Icons as components
const PlusCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClipboardListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const ChartBarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const CogIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const AdminDashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
);

// Sun and Moon icons for dark mode toggle
const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

// New-notification-alert dedupe state, keyed to the signed-in user.
//
// 🚨 MODULE SCOPE, NOT A ref. `<NotificationBell />` is mounted TWICE per
// page (mobile nav + desktop nav). Since 2026-09-24 both read ONE feed
// (`useNotificationFeedSource`, below), so only one fetch ever reaches this
// state; it stays at module scope so that a remount of the feed (a route
// that re-renders Layout) cannot reseed and either re-alert or go silent.
//
// Keyed by `userId` so a mid-session user switch (impersonation, or a
// logout/login that does not reload the page) reseeds from scratch rather
// than either alerting for a stranger's whole existing tray or silently
// swallowing genuinely-new rows because the ids happen not to collide.
//
// 🔀 WATERMARK, SINCE 2026-09-24 (the bell became a PAGE of 50). "Not in the
// last poll's ids" used to be the whole rule, which was right only while the
// list was every unread row. Now marking one page-1 row read slides the 51st —
// an OLD row — into page 1, and that rule would announce it as new. So a row
// alerts only if it is unseen AND newer (created_at, then id) than the newest
// row ever seen for this user. A re-surfaced invite is re-dated by the server
// (`resurfaceOrInsertInviteNotification`), so it still alerts.
let notificationAlertState = { userId: null, seeded: false, seenIds: new Set(), newest: null };

// `(created_at, id)` of a row, for the watermark. An unparseable date falls
// back to id alone, never to "always new".
function notificationStamp(n) {
  const t = Date.parse(n && n.created_at);
  return { t: Number.isFinite(t) ? t : null, id: Number(n && n.id) || 0 };
}
function isNewerStamp(a, b) {
  if (!b) return true;
  if (a.t !== null && b.t !== null && a.t !== b.t) return a.t > b.t;
  return a.id > b.id;
}

// The bell asks for a page; `X-Unread-Count` is the true total (server/routes/
// notifications.js, GET /user/:userId). A missing or malformed header — a proxy
// that strips it, a cross-origin dev setup, a test double — falls back to the
// page length, which is what the badge showed before.
function unreadCountFromResponse(res) {
  const raw = res && res.headers && typeof res.headers.get === 'function'
    ? res.headers.get('X-Unread-Count') : null;
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
}
// The head of a page in the server's own order (created_at DESC, id DESC).
function newestOf(rows) {
  let best = null;
  let bestStamp = null;
  (Array.isArray(rows) ? rows : []).forEach((n) => {
    const s = notificationStamp(n);
    if (isNewerStamp(s, bestStamp)) { best = n; bestStamp = s; }
  });
  return best;
}
// What the cheap poll compares: WHOSE tray (the user id — a same-browser
// identity swap can meet an identical count and head), the count, and the head
// row's id and raw created_at. The page and `?count_only=1` read the same column through the
// same driver, so on an unchanged tray the two are identical strings. A
// missing count (no header) never matches, which only costs a page fetch.
function feedSignature(userId, count, newest) {
  const head = newest ? `${newest.id}|${newest.created_at}` : 'none';
  return `${userId}|${count === null || count === undefined ? 'no-count' : count}|${head}`;
}
function nextBeforeFromResponse(res) {
  const raw = res && res.headers && typeof res.headers.get === 'function'
    ? res.headers.get('X-Next-Before') : null;
  return raw && /^[1-9][0-9]*$/.test(String(raw)) ? String(raw) : null;
}

// ONE SOURCE FOR BOTH BELLS (task POLL, 2026-09-24). `<NotificationBell />` is
// mounted twice (mobile header + desktop title bar, both always in the DOM),
// and each instance used to run its own fetch and its own timer — two
// identical requests per tick. The list, the count and the poll now live
// here, called ONCE by `Layout`; each bell reads them from this context and
// keeps only what is genuinely per-instance (open state, older rows, the
// Join prompt). What each bell RENDERS is unchanged.
const NotificationFeedContext = React.createContext(null);

function useNotificationFeedSource() {
  const location = useLocation();
  const { pendingInvites, pendingInvitesLoaded, refreshPendingInvites } = useDepartment();
  const toast = useToast();
  const [notifications, setNotifications] = useState([]);
  // The server's true unread total (X-Unread-Count); null = header absent, so
  // the badge falls back to the page length.
  const [serverUnreadCount, setServerUnreadCount] = useState(null);
  // Page 1's cursor, and the rows "Load older" has fetched beyond it. Older
  // rows live only while the tray is open; every poll refreshes page 1 alone.
  const [page1NextBefore, setPage1NextBefore] = useState(null);
  // Read through a ref so the poll callback does not change identity (and
  // restart the poll interval) every time the invite list changes.
  const pendingInvitesRef = useRef([]);
  pendingInvitesRef.current = Array.isArray(pendingInvites) ? pendingInvites : [];
  // The context OWNS the invite fetch; until its first load answers, an id
  // is not "unknown", merely "not loaded yet" — see `pendingInvitesLoaded`.
  const pendingInvitesLoadedRef = useRef(false);
  pendingInvitesLoadedRef.current = pendingInvitesLoaded === true;
  // Same reason: a harness that mocks `useDepartment()` hands a fresh function
  // every render, and a poll keyed on it would refetch on every render.
  const refreshPendingInvitesRef = useRef(refreshPendingInvites);
  refreshPendingInvitesRef.current = refreshPendingInvites;
  // Invite ids this feed has already asked the context to refresh for, so
  // an invite the server no longer lists (withdrawn, expired) costs ONE
  // refresh, never one per poll.
  const inviteRefreshAskedRef = useRef(new Set());

  // Alert the user to notifications NEW since the last poll (never the ones
  // already present on first load — see `notificationAlertState` above).
  //
  // MUTUALLY EXCLUSIVE fork, never both for the same arrival: an OS (browser)
  // notification when permission is granted AND the tab is hidden (the user
  // is away and would otherwise miss it); an in-app toast for every other
  // case — tab focused, permission denied/default, or no Notification API at
  // all.
  //
  // Don't spam: one new notification shows its own message; more than one
  // collapses to a single "N new notifications" summary, and that cap
  // applies identically to the OS path — one popup, never N.
  const alertNewNotifications = useCallback((newOnes) => {
    if (newOnes.length === 0) return;

    const summary = newOnes.length === 1
      ? newOnes[0].message
      : `${newOnes.length} new notifications`;

    const canUseOsNotification =
      ('Notification' in window) &&
      Notification.permission === 'granted' &&
      document.visibilityState === 'hidden';

    if (canUseOsNotification) {
      const osNotification = new Notification('CrewForge', {
        body: summary,
        tag: 'crewforge-new-notifications',
      });
      osNotification.onclick = () => { window.focus(); };
      return;
    }

    toast.info(summary);
  }, [toast]);

  // Page 1 as the tray shows it, and the answer the cheap poll compares
  // against (see `feedSignature`). Everything a page fetch does — the list,
  // the badge, the invite refresh, the new-row alert — happens HERE, whichever
  // request brought the page.
  const lastSignatureRef = useRef(null);
  const applyPage = useCallback((userId, res, data) => {
    const pageMeta = { count: unreadCountFromResponse(res), nextBefore: nextBeforeFromResponse(res) };
    if (!Array.isArray(data)) return;
    setNotifications(data);
    setServerUnreadCount(pageMeta.count);
    setPage1NextBefore(pageMeta.nextBefore);

    // A `department_invite` notification naming an invite the context
    // does not know yet → refresh the invite list, so the switcher shows
    // the new department without waiting for a reload. No timer of its
    // own: it rides this existing poll.
    const known = new Set(pendingInvitesRef.current.map(i => String(i.id)));
    const unknown = !pendingInvitesLoadedRef.current ? [] : data.filter(n => n.notification_type === 'department_invite'
      && n.department_invite_id != null
      && !known.has(String(n.department_invite_id))
      && !inviteRefreshAskedRef.current.has(String(n.department_invite_id)));
    if (unknown.length > 0 && typeof refreshPendingInvitesRef.current === 'function') {
      unknown.forEach(n => inviteRefreshAskedRef.current.add(String(n.department_invite_id)));
      refreshPendingInvitesRef.current();
    }

    if (notificationAlertState.userId !== userId) {
      // First fetch ever, or a different user than last time this module
      // saw — reseed from scratch so THIS user's pre-existing rows don't
      // alert either.
      notificationAlertState = { userId, seeded: false, seenIds: new Set(), newest: null };
    }

    const currentIds = new Set(data.map(n => n.id));
    const watermark = notificationAlertState.newest;
    if (!notificationAlertState.seeded) {
      // First fetch for this user: seed silently, no alert.
      notificationAlertState.seeded = true;
    } else {
      // Unseen AND newer than anything seen — see the WATERMARK note above.
      const newOnes = data.filter(n => !notificationAlertState.seenIds.has(n.id)
        && isNewerStamp(notificationStamp(n), watermark));
      alertNewNotifications(newOnes);
    }
    notificationAlertState.seenIds = currentIds;
    notificationAlertState.newest = data.reduce(
      (best, n) => { const s = notificationStamp(n); return isNewerStamp(s, best) ? s : best; },
      watermark
    );
    lastSignatureRef.current = feedSignature(userId, pageMeta.count, newestOf(data));
  }, [alertNewNotifications]);

  // The PAGE: newest 50 + pinned invites, with the count in X-Unread-Count.
  const fetchNotifications = useCallback(() => {
    const userId = localStorage.getItem('sfx_user_id');
    if (!userId) return Promise.resolve();
    let response = null;
    return apiGet(`/api/notifications/user/${encodeURIComponent(userId)}`)
      .then(res => { response = res; return res.json(); })
      .then(data => applyPage(userId, response, data))
      .catch(() => {});
  }, [applyPage]);

  // 🆕 THE CHEAP POLL (task POLL, 2026-09-24). `?count_only=1` answers the
  // count and the head of the list and no rows (server/routes/notifications.js),
  // a few dozen bytes; the page is fetched only when that answer differs from
  // the page on screen. A new row moves the head, a row read elsewhere moves
  // the count, a re-dated invite moves the head's created_at — each fetches the
  // page, and the page fetch fires the alert exactly as before. A server that
  // ignores the parameter answers with the page itself, which is used as one.
  // ⚠️ Not seen: a change that moves neither (one row read AND another older
  // one surfacing between two polls); the next change that does move one
  // catches it.
  const checkForChanges = useCallback(() => {
    const userId = localStorage.getItem('sfx_user_id');
    if (!userId) return Promise.resolve();
    let response = null;
    return apiGet(`/api/notifications/user/${encodeURIComponent(userId)}?count_only=1`)
      .then(res => { response = res; return res.json(); })
      .then(body => {
        if (Array.isArray(body)) return applyPage(userId, response, body);
        if (!body || !Number.isInteger(body.unread_count) || body.unread_count < 0) return undefined;
        if (feedSignature(userId, body.unread_count, body.newest) === lastSignatureRef.current) return undefined;
        return fetchNotifications();
      })
      .catch(() => {});
  }, [applyPage, fetchNotifications]);

  // The page once on mount — the tray needs rows before any change is seen.
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // The poll: the shared hook (no stacking, silent errors, an immediate check
  // on returning to the tab) at the owner's 30 s, imported, never a literal.
  // 🔀 IT KEEPS TICKING IN A HIDDEN TAB, AND THAT OVERRIDES THE OWNER'S ASK
  //    ("pause while the tab is hidden") BY CONTROLLER RULING 2026-09-24: the
  //    OS popup (`alertNewNotifications`) fires ONLY while the tab is hidden,
  //    so pausing would silence it for everyone who granted permission. The
  //    tick is the count-only check (a few dozen bytes); a change fetches the
  //    page and the popup has its row.
  usePollingRefresh(checkForChanges, { intervalMs: POLL_INTERVAL_MS, skipWhenHidden: false });

  // Re-check on navigation — on a CHANGE of path only, and through the cheap
  // check (the page follows only if something changed). The mount is already
  // covered by the page fetch above; running here too made every page load
  // ask twice.
  const lastFetchedPathRef = useRef(location.pathname);
  useEffect(() => {
    if (lastFetchedPathRef.current === location.pathname) return;
    lastFetchedPathRef.current = location.pathname;
    checkForChanges();
  }, [location.pathname, checkForChanges]);

  return {
    notifications, setNotifications, serverUnreadCount, setServerUnreadCount,
    page1NextBefore, setPage1NextBefore, fetchNotifications,
  };
}

function NotificationBell() {
  const {
    notifications, setNotifications, serverUnreadCount, setServerUnreadCount,
    page1NextBefore, setPage1NextBefore, fetchNotifications,
  } = React.useContext(NotificationFeedContext);
  const [olderNotifications, setOlderNotifications] = useState([]);
  const [olderNextBefore, setOlderNextBefore] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobilePopupRef = useRef(null);
  // THIS bell instance's button: where focus returns when a Join prompt opened
  // from one of its notifications closes (the row has unmounted by then).
  const bellButtonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    switchDepartment, assignments, pendingInvites, refreshPendingInvites, hasDirtyEdit,
    isAdmin, capabilitiesFor, activeDepartment,
  } = useDepartment();
  const toast = useToast();

  // 🆕 DEPARTMENT-INVITE NOTIFICATIONS (join-a-second-department, P5). The
  // Join prompt is PER BELL INSTANCE (the bell is mounted twice, mobile and
  // desktop) — only the instance that was clicked opens one.
  const [joinInvite, setJoinInvite] = useState(null);
  // An invite notification clicked while an order page held a dirty edit:
  // the unsaved-changes dialog first, the Join prompt only on Continue.
  const [dirtyJoinInvite, setDirtyJoinInvite] = useState(null);
  // The notification that opened the prompt — marked read only if the
  // invite turns out to be gone (see `handleNotificationClick`).
  const [joinNotifId, setJoinNotifId] = useState(null);
  // The Join prompt looks a clicked invite up in the latest list.
  const pendingInvitesRef = useRef([]);
  pendingInvitesRef.current = Array.isArray(pendingInvites) ? pendingInvites : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inButton = dropdownRef.current && dropdownRef.current.contains(e.target);
      const inMobilePopup = mobilePopupRef.current && mobilePopupRef.current.contains(e.target);
      if (!inButton && !inMobilePopup) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = (notifId) => {
    apiFetch(`/api/notifications/${notifId}/read`, { method: 'PATCH' })
      .then(() => {
        setNotifications(prev => prev.filter(n => n.id !== notifId));
        setOlderNotifications(prev => prev.filter(n => n.id !== notifId));
        // The next poll's count is the truth (it differs, so the page is
        // re-fetched); this keeps the badge honest until then.
        setServerUnreadCount(prev => (prev === null ? prev : Math.max(0, prev - 1)));
      })
      .catch(() => {});
  };

  // "Load older": the next page after the last row shown. Older rows are
  // dropped when the tray closes (below), so an open tray is the only place
  // they live and a poll never has to reconcile them.
  const olderCursor = olderNotifications.length > 0 ? olderNextBefore : page1NextBefore;
  const loadOlder = async () => {
    const userId = localStorage.getItem('sfx_user_id');
    if (!userId || !olderCursor || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await apiGet(`/api/notifications/user/${encodeURIComponent(userId)}?before=${encodeURIComponent(olderCursor)}`);
      if (!res || !res.ok) throw new Error('older page refused');
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error('older page malformed');
      setOlderNotifications(prev => {
        const have = new Set(prev.map(n => n.id));
        return [...prev, ...data.filter(n => !have.has(n.id))];
      });
      setOlderNextBefore(nextBeforeFromResponse(res));
    } catch {
      toast.error('Could not load older notifications. Try again.');
    } finally {
      setLoadingOlder(false);
    }
  };
  useEffect(() => {
    if (!isOpen) {
      setOlderNotifications([]);
      setOlderNextBefore(null);
    }
  }, [isOpen]);

  // 🔀 SWITCH ONLY ONCE THE NAVIGATION IS ALLOWED, WHEN A PAGE HOLDS UNSAVED
  // WORK (R14, 2026-09-15). This handler switched department and THEN navigated.
  // With a leave guard armed (`hooks/useUnsavedChangesGuard.js`) the navigation
  // is held behind "Leave without saving?" — but the switch had already
  // happened, so pressing Stay left the user in the NEW department with a form
  // that the switch had just reset (New Order drops its typing and discards its
  // uploads on a switch). So while a guard is armed the switch is PARKED and
  // applied by the effect below only when the location actually reaches the
  // notification's page (Leave); any other location change drops it (Stay,
  // then going somewhere else). With no guard armed nothing changes.
  // Keyed on `location` ALONE (the ref carries the latest `switchDepartment`):
  // its identity changes on a context refetch, and re-running on that would
  // drop a parked switch while the dialog is still open.
  const pendingSwitchRef = useRef(null);
  const switchDepartmentRef = useRef(switchDepartment);
  switchDepartmentRef.current = switchDepartment;
  useEffect(() => {
    const pending = pendingSwitchRef.current;
    if (!pending) return;
    pendingSwitchRef.current = null;
    if (location.pathname === pending.pathname) switchDepartmentRef.current(pending.departmentId);
  }, [location]);

  // 🆕 A `department_invite` notification opens the Join prompt and does NOT
  // navigate (it carries `department_id: null` — there is nowhere to go until
  // the user joins). A manual × dismissal only clears the TRAY: the invite
  // itself lives in `pendingInvites`, so the switcher row survives it
  // (R16: "the invite should never disapear").
  const openJoinForInvite = async (inviteId, notificationId) => {
    const key = String(inviteId);
    setJoinNotifId(notificationId ?? null);
    let invite = pendingInvitesRef.current.find(i => String(i.id) === key);
    if (!invite && typeof refreshPendingInvites === 'function') {
      const fresh = await refreshPendingInvites();
      // "Could not ask" is NOT "gone" (review R1): leave the notification
      // unread and say so in a way that invites a retry.
      if (!fresh || fresh.ok !== true) {
        toast.error('Couldn’t load your invites — try again');
        return;
      }
      invite = (fresh.invites || []).find(i => String(i.id) === key);
    }
    if (!invite) {
      // The invite is gone — nothing left to keep the notification for.
      if (notificationId != null) markAsRead(notificationId);
      toast.info('That invitation is no longer open.');
      return;
    }
    // Joining lands you in another department, so it runs the SAME
    // unsaved-edit check as the switcher (controller ruling, 2026-09-23).
    if (hasDirtyEdit?.()) {
      setDirtyJoinInvite(invite);
      return;
    }
    setJoinInvite(invite);
  };

  // THE one place this bell asks "where does this row open" — shared by the
  // click handler and `renderNotificationList`'s affordance (2026-10-02),
  // so the two can never disagree about which rows are dead clicks. A
  // `department_invite` row is always "clickable" here (it opens the Join
  // prompt rather than navigating); every other type defers to
  // `notificationTarget()`, which answers `null` for a type carrying no
  // identifying data at all (`department_warning`, and any FUTURE type this
  // server writes that nobody has wired a destination for).
  const resolveNotificationTarget = (notification) => {
    if (notification.notification_type === 'department_invite') return null;
    return notificationTarget(notification, {
      isAdmin, assignments, activeDepartmentId: activeDepartment?.department_id ?? null, capabilitiesFor,
      userId: localStorage.getItem('sfx_user_id'),
    });
  };
  const notificationIsClickable = (notification) => notification.notification_type === 'department_invite'
    || !!resolveNotificationTarget(notification);

  const handleNotificationClick = (notification) => {
    // 🔀 "BELL KEEPS IT" (owner ruling, 2026-09-23 bedtime). A
    // `department_invite` notification is NOT marked read on click: it stays
    // in the bell until the person Joins or the invite ends (the server marks
    // it read on accept, withdraw and lapse). Below 640px the switcher is not
    // rendered at all, so on a phone the bell is the only way back to an
    // invite. The one client-side mark-read is when the invite turns out to
    // be GONE (accept answers 404/410, or it is no longer listed).
    if (notification.notification_type === 'department_invite') {
      setIsOpen(false);
      openJoinForInvite(notification.department_invite_id, notification.id);
      return;
    }
    markAsRead(notification.id);
    // A hire notification opens the HIRE (owner, 2026-09-27) — the rule, and
    // the no-seat fallback to the order page, live in `utils/notificationTarget.js`.
    // A notification with no destination at all (see `resolveNotificationTarget`
    // above) still reaches `markAsRead` above; `target` is simply falsy below,
    // so `navigate` is skipped — clicking a non-clickable row still clears it.
    const target = resolveNotificationTarget(notification);
    // Switch to the notification's production/department before navigating
    const needsSwitch = !!(notification.department_id && assignments && assignments.length > 0
      && assignments.find(a => a.department_id === notification.department_id));
    if (needsSwitch && target && getMountedNavigationBlockerCount() > 0) {
      // `location.pathname` never carries the `?hire=` part, so compare the path alone.
      pendingSwitchRef.current = { departmentId: notification.department_id, pathname: target.split('?')[0] };
      navigate(target);
      setIsOpen(false);
      return;
    }
    if (needsSwitch) {
      switchDepartment(notification.department_id);
    }
    if (target) {
      navigate(target);
    }
    setIsOpen(false);
  };

  // "Mark all as read" — ONE request.
  //
  // ═════════════════════════════════════════════════════════════════════════
  // 🚨 IT WAS ONE PATCH PER NOTIFICATION IN A SINGLE `Promise.all`, AND THE
  //    NUMBER OF NOTIFICATIONS IS UNBOUNDED. THAT WAS THE WHOLE BUG.
  // ═════════════════════════════════════════════════════════════════════════
  //
  // Reported on staging 2026-08-11 by a plain `user` with 9,659 unread rows:
  // the tray answered *"4959 of 6342 notifications could not be cleared"*.
  //
  // 🔑 THE MESSAGE WAS HONEST AND THE DIAGNOSIS IT INVITES IS WRONG. Not one of
  //    those refusals was an authorisation refusal. That caller holds
  //    `canManageDepartmentOrders` in no department, so every row they are shown
  //    is a `target_type = 'user'` row addressed to them by id, and the server
  //    clears those unconditionally. What failed was the fan-out: thousands of
  //    concurrent requests against a 20-client Postgres pool with a 5s
  //    connection timeout, and `apiFetch` resolves on a 5xx exactly as it does
  //    on a 4xx, so every one of them counted as "refused".
  //
  // 🔻 THE PER-ROW LOOP AND ITS PARTIAL-FAILURE TOAST ARE THEREFORE DELETED, NOT
  //    RETAINED AS A FALLBACK, and the deletion is the fix rather than a tidy-up.
  //    Their stated purpose was to be a BACKSTOP against the LIST and the CLEAR
  //    drifting apart — a real hazard, which had already happened twice. But a
  //    backstop that fires N requests to detect a mismatch is a denial of
  //    service wearing a safety harness, and it can only ever report the drift
  //    to a user who can do nothing about it. That job moved to where it can
  //    actually be enforced: `POST /api/notifications/mark-all-read` and
  //    `GET /api/notifications/user/:userId` are built from ONE shared predicate
  //    in `server/routes/notifications.js`, so the bulk clear writes exactly the
  //    rows the tray showed BY CONSTRUCTION, and
  //    `server/tests/matrix/notifications-shown-set-equals-clearable-set.test.js`
  //    reds if the two ever diverge again.
  //
  // 🪤 `res.ok` IS STILL READ, AND FOR THE ORIGINAL REASON. `apiFetch` resolves
  //    on a 4xx/5xx and only rejects on a network error, so `await` alone proves
  //    nothing — a 500 here must leave the rows on screen rather than empty a
  //    tray the server never touched. The single-request shape makes that
  //    simpler, not unnecessary.
  //
  // The refetch is what settles the count: `marked` is the number of rows the
  // UPDATE hit, and rows can arrive between the click and the response, so the
  // server's next answer is the truth rather than our arithmetic.
  const markAllAsRead = async () => {
    if (notifications.length === 0) return;

    let res;
    try {
      res = await apiFetch('/api/notifications/mark-all-read', { method: 'POST' });
    } catch {
      res = null;
    }

    if (!res || !res.ok) {
      // Leave the tray OPEN and the rows ON SCREEN. The bell keeps telling the
      // truth instead of emptying and reverting on the next poll, which is
      // the failure mode the per-row version was written to stop and which this
      // version must not reintroduce.
      toast.error('Could not clear your notifications. They are still listed below.');
      return;
    }

    setNotifications([]);
    setServerUnreadCount(0);
    setPage1NextBefore(null);
    setIsOpen(false);
    fetchNotifications();
  };

  // What the tray lists: page 1 (refreshed by every poll) plus whatever "Load
  // older" fetched, without repeating a row a poll has since pulled into page 1.
  const shownNotifications = React.useMemo(() => {
    if (olderNotifications.length === 0) return notifications;
    const inPage1 = new Set(notifications.map(n => n.id));
    return [...notifications, ...olderNotifications.filter(n => !inPage1.has(n.id))];
  }, [notifications, olderNotifications]);

  // 🔀 THE BADGE READS THE SERVER'S TRUE TOTAL (X-Unread-Count) since
  // 2026-09-24, because the list is now a page. `.length` is the fallback only
  // when the header is absent. The `9+` cap is unchanged (controller ruling).
  const unreadCount = serverUnreadCount !== null
    ? Math.max(serverUnreadCount, shownNotifications.length)
    : shownNotifications.length;
  const hasOlder = !!olderCursor;

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Group notifications by production.
  //
  // Keyed on `production_id`, NEVER on `production_name`. A name is a human
  // label — it is user-editable and NOT unique. Two productions sharing a name
  // are legitimate and expected (`createPersonalSpace` gives every user a "My
  // Space" production, so duplicates are the commonest shape in the database),
  // and grouping by name collapsed them into a single tray entry — one heading
  // claiming notifications from two different shows.
  //
  // Notifications with no production (`production_id` null — no linked order,
  // or an order whose department has no production) share the 'General' bucket:
  // that is a genuine "no identity", not a name collision.
  const groupedNotifications = React.useMemo(() => {
    const groups = new Map();
    shownNotifications.forEach((n) => {
      const key = n.production_id != null ? `p${n.production_id}` : 'General';
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          production_id: n.production_id != null ? n.production_id : null,
          production_name: n.production_name || 'General',
          company_name: n.company_name || '',
          items: []
        });
      }
      groups.get(key).items.push(n);
    });
    // 🔀 A group holding a still-pending department_invite sorts FIRST (task
    // BELLFU, owner ruling "the bell keeps pending invites" — kept must also
    // mean SEEN). No new field is needed: every writer that ends an invite's
    // pending state (accept/withdraw/lapse, the nightly sweep) flips the
    // notification's `is_read` in the SAME transaction, and this endpoint
    // returns unread rows only — so a `department_invite`-typed row THIS
    // endpoint ever returns is, by construction, still pending. Below that,
    // groups sort by size descending, unchanged.
    return Array.from(groups.values()).sort((a, b) => {
      const aPinned = a.items.some((n) => n.notification_type === 'department_invite');
      const bPinned = b.items.some((n) => n.notification_type === 'department_invite');
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return b.items.length - a.items.length;
    });
  }, [shownNotifications]);

  // Render grouped notification list (shared between mobile and desktop)
  const renderNotificationList = () => {
    if (unreadCount === 0) {
      return (
        <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No new notifications
        </div>
      );
    }

    return groupedNotifications.map((group, groupIdx) => (
      <div key={group.key} data-testid={`notif-group-${group.key}`}>
        {/* Production heading - only show if there are multiple groups or a known production */}
        {(groupedNotifications.length > 1 || group.production_name !== 'General') && (
          // 🎨 Owner 2026-09-25: in dark mode the banner was a heavy navy that turned subtle on
          // hover, and a hover change suggests the banner is clickable (it is not). That hover
          // was an ACCIDENT of two global index.css overrides: `.dark:bg-blue-900/30:not(:hover)`
          // forced navy at rest, and `html.dark .bg-blue-50` (a 10% blue tint) showed through on
          // hover. The banner now carries the subtle colour ALWAYS, as SOLID arbitrary values:
          // neither override class is present, so there is no hover change, and a solid fill
          // stops rows showing through this sticky header as they scroll under it.
          // #eff6ff = blue-50 (light). #213250 = the 10% tint of rgb(59,130,246) over the dark
          // tray's real #1e293b (`html.dark .bg-white`).
          <div className={`sticky top-0 z-10 px-4 py-2 bg-[#eff6ff] dark:bg-[#213250] border-b border-blue-100 dark:border-blue-800 ${groupIdx > 0 ? 'border-t-2 border-t-gray-300 dark:border-t-gray-600' : ''}`}>
            <p className="text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wide">
              {group.production_name}
            </p>
            {group.company_name && (
              <p className="text-xs text-blue-600 dark:text-blue-400">{group.company_name}</p>
            )}
            <span className="text-xs text-blue-500 dark:text-blue-400 font-medium">
              {group.items.length} notification{group.items.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {group.items.map((n) => {
          // A row with nowhere to go (2026-10-02: `department_warning` today,
          // and any future type nobody has wired a destination for) must not
          // LOOK clickable — no pointer cursor, no hover fill — while the
          // body stays a real `<button>` so a click still reaches `markAsRead`
          // (see `handleNotificationClick`'s comment). Colour is never the
          // only channel here either way: the row's TEXT says what happened.
          const clickable = notificationIsClickable(n);
          return (
          // Row is a flex CONTAINER, not a single <button>: the body must stay
          // clickable (navigate-or-dismiss, unchanged) while the "×" is a SECOND,
          // independent control. A <button> cannot nest a <button> (invalid HTML;
          // the browser closes the outer one at the inner tag), which is why this
          // is a <div> with two sibling buttons rather than the old single-button
          // row wearing a second element inside it.
          <div
            key={n.id}
            className={`w-full flex items-stretch border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${clickable ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : ''}`}
          >
            <button
              type="button"
              onClick={() => handleNotificationClick(n)}
              className={`flex-1 min-w-0 text-left px-4 py-3 ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <p className="text-sm text-gray-900 dark:text-white">{n.message}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatTime(n.created_at)}</p>
            </button>
            {/* Dismiss = the same markAsRead(id) the body click already runs first —
                "read" already means "removed from the tray" here, so this is not a
                new endpoint or a new concept, just a direct route to it that skips
                the navigation. stopPropagation keeps a click here from bubbling into
                the row and firing handleNotificationClick as well. */}
            {/* 🔀 NO × ON A `department_invite` ROW ("bell keeps it", review
                R2, 2026-09-23). Dismissing marks read, and a read invite
                notification is gone from the bell — on a phone, where the
                switcher is hidden, that made the invite unreachable. The row
                leaves the bell when the person Joins or the invite ends. */}
            {n.notification_type !== 'department_invite' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
              aria-label={`Dismiss: ${n.message}`}
              title="Dismiss"
              className="flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center text-lg leading-none text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              ×
            </button>
            )}
          </div>
          );
        })}
      </div>
    ));
  };

  // The tray heading's count. When the tray shows every unread row it keeps its
  // old wording; when it shows a page it must not imply that the production
  // count covers rows it has not fetched, so it says how many are shown.
  const trayCountLabel = shownNotifications.length < unreadCount
    ? `(${unreadCount.toLocaleString('en-GB')} unread, newest ${shownNotifications.length} shown)`
    : `(${unreadCount} across ${groupedNotifications.length} production${groupedNotifications.length !== 1 ? 's' : ''})`;

  // "Load older" — the last thing in the scroll area, so it is reached by
  // scrolling past the rows it extends. Absent when nothing older remains.
  const renderLoadOlder = () => {
    if (!hasOlder || shownNotifications.length === 0) return null;
    return (
      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-center">
        <button
          type="button"
          onClick={loadOlder}
          disabled={loadingOlder}
          data-testid="notifications-load-older"
          className={`${BTN_XS.secondary} disabled:opacity-60`}
        >
          {loadingOlder ? 'Loading…' : 'Load older notifications'}
        </button>
      </div>
    );
  };

  // Request OS-notification permission on the gesture that OPENS the tray —
  // never on mount. Browsers block or penalise a permission prompt fired
  // without a user gesture, so a click here is the earliest legitimate
  // moment. Guarded by `'Notification' in window` for browsers that lack the
  // API entirely, and only fires while permission is still `'default'` (a
  // prior grant/denial is never re-prompted).
  const handleBellToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening && ('Notification' in window) && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={bellButtonRef}
        onClick={handleBellToggle}
        className="relative p-2 rounded-lg bg-white/10 hover:bg-white/20 text-blue-200 hover:text-white transition-colors"
        title="Notifications"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: fixed full-width popup */}
          <div ref={mobilePopupRef} className="lg:hidden fixed left-2 right-2 top-[calc(3.5rem+var(--impersonation-offset,0px))] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[100] max-h-[80vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                    {trayCountLabel}
                  </span>
                )}
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[70vh]">
              {renderNotificationList()}
              {renderLoadOlder()}
            </div>
          </div>
          {/* Desktop: absolute dropdown - positioned left-0 so it extends into content area */}
          <div className="hidden lg:block absolute left-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[100] max-h-[28rem] overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                    {trayCountLabel}
                  </span>
                )}
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-96">
              {renderNotificationList()}
              {renderLoadOlder()}
            </div>
          </div>
        </>
      )}

      {/* Department-invite prompts — this bell instance only. The unsaved-
          changes copy is the switcher's, imported, so the two never drift.
          Both portal to <body> (`toBody`) so no header stacking context can
          out-paint the overlay. */}
      {toBody(<ConfirmDialog
        open={!!dirtyJoinInvite}
        title={UNSAVED_SWITCH_TITLE}
        message={UNSAVED_SWITCH_MESSAGE}
        confirmLabel="Continue"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => { const inv = dirtyJoinInvite; setDirtyJoinInvite(null); setJoinInvite(inv); }}
        onCancel={() => setDirtyJoinInvite(null)}
      />)}
      {joinInvite && (
        <JoinDepartmentDialog
          invite={joinInvite}
          onClose={() => setJoinInvite(null)}
          returnFocusRef={bellButtonRef}
          onGone={() => { if (joinNotifId != null) markAsRead(joinNotifId); }}
        />
      )}
    </div>
  );
}

const UserCogIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Department dashboard icon (users group + chart)
const DeptDashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

// Clipboard check icon for Approvals
const ClipboardCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

// Hires icon (cube / equipment)
const HiresIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

// APPROVAL_ROLES and DEPARTMENT_ROLES imported from constants/statuses.js

const TeamIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
  </svg>
);

// Grading icon — academic cap (mortarboard) for grade progression
const GradingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14v6" />
  </svg>
);

const userNavItems = [
  { path: '/orders/new', label: 'New Order', icon: PlusCircleIcon },
  { path: '/orders', label: 'My Orders', icon: ClipboardListIcon },
  { path: '/hires', label: 'My Hires', icon: HiresIcon },
  { path: '/dashboard', label: 'My Dashboard', icon: ChartBarIcon },
  { path: '/settings', label: 'My Settings', icon: UserCogIcon },
];

// Grading sits at the very bottom of the sidebar (rendered as a standalone
// item after every section), not within the personal nav group.
const gradingNavItem = { path: '/grading', label: 'Grading', icon: GradingIcon };

// Gag Breakdown icon (film strip)
const GagBreakdownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
  </svg>
);

// The Planning section (Round 8). Breakdown is the section landing.
// 🔻 "Manage Teams" is HIDDEN since 2026-10-02 (Gags/teams/tasks Stage 1: a
// team is only ever the team on a gag, managed in the gag's panel); its route
// redirects to My Tasks until Stage 2 deletes the old team code.
// Timesheets icon (clock). Planning → Timesheets, S3b 2026-10-24.
const TimesheetsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
// Visible to EVERY member of a real department (owner ruling 2026-10-24) — NOT
// budget-gated; the page shows the manager screen on canManageBudget.
const timesheetsNavItem = { path: '/timesheets', label: 'Timesheets', icon: TimesheetsIcon };

const teamsBaseItems = [
  // Visible to ALL users — the breakdown is full-transparency within a department.
  { path: '/teams/breakdown', label: 'Breakdown', icon: GagBreakdownIcon },
  { path: '/teams/my-tasks', label: 'My Tasks', icon: ClipboardListIcon },
];

const AnalyticsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

// Budget icon (currency/spreadsheet)
const BudgetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
  </svg>
);

// Marketplace icons
const MarketplaceSearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75v-2.25a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v2.25c0 .414.336.75.75.75z" />
  </svg>
);
const MarketplaceListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
  </svg>
);
const MarketplacePlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const marketplaceNavItems = [
  { path: '/hireforge', label: 'Browse Equipment', icon: MarketplaceSearchIcon },
  { path: '/hireforge/my-listings', label: 'My Listings', icon: MarketplaceListIcon },
  { path: '/hireforge/listings/new', label: 'Add Listing', icon: MarketplacePlusIcon },
];

// Risk Assessment icon — shield with checkmark
const RiskAssessmentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
  </svg>
);

const riskAssessmentNavItems = [
  { path: '/risk-assessments',          label: "My RA's",         icon: RiskAssessmentIcon },
  { path: '/risk-assessments/library',  label: 'Template Library', icon: RiskAssessmentIcon },
];

// One literal, three call sites: the admin section, the buyer branch and the
// capability-holding-supervisor branch of buildRoleNavItems. Extracted rather
// than copied a third time (CLAUDE.md, "Code Unification Principle").
const adminSettingsNavItem = { path: '/admin/settings', label: 'Admin Settings', icon: CogIcon };

const adminNavItems = [
  { path: '/department/orders', label: 'All Orders', icon: ShieldCheckIcon },
  { path: '/admin/hires', label: 'Admin Hires', icon: HiresIcon },
  { path: '/admin/dashboard', label: 'Admin Dashboard', icon: AdminDashIcon },
  { path: '/admin/analytics', label: 'Site Analytics', icon: AnalyticsIcon },
  { path: '/budget/overview', label: 'Budgeting', icon: BudgetIcon },
  adminSettingsNavItem,
];

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { titleOverride, summary: pageSummary } = usePageHeader();
  // Log every route change as a semantic "page view" event so the
  // admin analytics feed reflects user journeys, not background polls.
  usePageViewLogger();
  // The bell's list, count and poll — ONE fetch for both bell instances.
  const notificationFeed = useNotificationFeedSource();
  const { user: authUser, logout, impersonating, originalUser, exitImpersonation } = useAuth();
  // The impersonation banner is `fixed`, so the page must make room for it or
  // it sits on top of the header and the bell (measured 2026-09-24: a real
  // click on the bell hit the banner at 1440px AND 400px). Its height is
  // MEASURED, never assumed: a long name wraps it to two lines on a phone.
  // The room is made at the TOP of the page only; once the desktop title bar
  // sticks (`sticky top-0`) it slides under the banner again, which is why the
  // banner itself is also click-through (see the banner below).
  const [impersonationBannerRef, impersonationBannerHeight] = useMeasuredHeight();
  const showImpersonationBanner = !!(impersonating && originalUser);
  const { isAdmin, isBuyer, canViewBudget, canManageStores, capabilities, activeRole, activeDepartment, assignments, holdsDepartmentSettingsSomewhere, ownsProductionLabel, loading: deptLoading } = useDepartment();
  // 📱 Phone: the production name in the header opens the switcher (owner 2026-10-04).
  const [phoneSwitcherOpen, setPhoneSwitcherOpen] = useState(false);
  const activeAssignment = activeDepartment && assignments
    ? assignments.find(a => a.department_id === activeDepartment.department_id)
    : null;
  const activeProductionName = activeAssignment
    ? spaceLabelFor(
        {
          is_personal: activeAssignment.production_is_personal,
          owner_user_id: activeAssignment.production_owner_user_id,
          production_name: activeAssignment.production_name,
          company_name: activeAssignment.company_name,
        },
        authUser
      )
    : (isAdmin && (!assignments || assignments.length === 0) ? 'All Productions' : '');
  // The viewer's own sub-department in the active department, shown beside the
  // production name in BOTH headers — owner 2026-10-09 (demo run-through):
  // "include the sub department for clarity … 'Project Aurora (Workshop)'".
  // From `GET /api/roles/my-assignments` (`sd.name AS sub_department_name`);
  // absent when the seat has no sub-department, and never on a My Space.
  const activeSubDepartmentName = activeAssignment && !activeAssignment.production_is_personal
    ? (activeAssignment.sub_department_name || '')
    : '';
  const [adminNotifCount, setAdminNotifCount] = useState(0);
  const [adminNotifColor, setAdminNotifColor] = useState('red');
  const [adminUrgency, setAdminUrgency] = useState({ red: 0, yellow: 0, green: 0 });
  const [urgencySummary, setUrgencySummary] = useState({ myOrders: { count: 0, color: 'green', red: 0, amber: 0 }, deptOrders: { count: 0, color: 'green', red: 0, amber: 0 }, waitingByDepartment: {} });
  // Owner ruling, 2026-09: the `/department/orders` numeric badge is
  // coloured by the WORSE of the admin urgency colour and the tight
  // lead-time dot colour it otherwise suppresses — a green count must not
  // hide red rows underneath it. See `utils/urgencyColour.js`.
  const deptOrdersBadgeColour = worstUrgencyColour(adminNotifColor, urgencySummary.deptOrders.color);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  // Overview removed (Round 8) — the Planning section is always the three base
  // items (Teams / My Tasks / Breakdown); no more conditional Overview gate.
  const teamsNavItems = teamsBaseItems;
  const [activeAccountId, setActiveAccountId] = useState(() => {
    return localStorage.getItem('sfx_active_account') || '';
  });
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [hireUrgency, setHireUrgency] = useState({ myHires: { count: 0, color: null }, deptHires: { count: 0, color: null } });
  const [approvalUrgency, setApprovalUrgency] = useState({ color: 'green', red: 0, yellow: 0 });
  // Derive userRole from context: admin from isAdmin, otherwise activeRole or fallback to localStorage
  const userRole = isAdmin ? 'admin' : (activeRole || localStorage.getItem('sfx_user_role') || 'user');
  const [userProductions, setUserProductions] = useState([]); // Productions assigned to current user
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('sfx_dark_mode');
    if (stored !== null) return stored === 'true';
    // Check system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  // All users can collapse/expand sidebar sections, persisted in localStorage
  const [myOrdersCollapsed, setMyOrdersCollapsed] = useState(() => {
    const saved = localStorage.getItem(userScopedKey('sfx_sidebar_my_orders_collapsed'));
    if (saved !== null) return saved === 'true';
    // Default: collapsed for admin and buyer roles, expanded for others
    const role = localStorage.getItem('sfx_user_role') || 'user';
    return role === 'admin' || role === 'buyer';
  });
  const [roleSectionCollapsed, setRoleSectionCollapsed] = useState(() => {
    const saved = localStorage.getItem(userScopedKey('sfx_sidebar_role_section_collapsed'));
    return saved === 'true';
  });
  const [marketplaceCollapsed, setMarketplaceCollapsed] = useState(() => {
    const saved = localStorage.getItem('sfx_sidebar_marketplace_collapsed');
    return saved === 'true';
  });
  const [riskAssessmentsCollapsed, setRiskAssessmentsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sfx_sidebar_ra_collapsed');
    return saved === 'true';
  });
  const [teamsCollapsed, setTeamsCollapsed] = useState(() => {
    const saved = localStorage.getItem(userScopedKey('sfx_sidebar_teams_collapsed'));
    if (saved !== null) return saved === 'true';
    // Default: collapsed for buyer role, expanded for others
    const role = localStorage.getItem('sfx_user_role') || 'user';
    return role === 'buyer';
  });
  const [testAccountsCollapsed, setTestAccountsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sfx_sidebar_test_accounts_collapsed');
    return saved === 'true';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sfx_sidebar_collapsed');
    return saved === 'true';
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sfx_sidebar_collapsed', String(next));
      return next;
    });
  };

  // Listen for role changes via custom event to update collapsed state and account ID
  useEffect(() => {
    const handleRoleChange = () => {
      setActiveAccountId(localStorage.getItem('sfx_active_account') || '');
      // Restore collapsed states from localStorage on role change
      const role = localStorage.getItem('sfx_user_role') || 'user';
      const savedMyOrders = localStorage.getItem(userScopedKey('sfx_sidebar_my_orders_collapsed'));
      if (savedMyOrders !== null) {
        setMyOrdersCollapsed(savedMyOrders === 'true');
      } else {
        // Default: collapsed for admin and buyer, expanded for others
        setMyOrdersCollapsed(role === 'admin' || role === 'buyer');
      }
      const savedTeams = localStorage.getItem(userScopedKey('sfx_sidebar_teams_collapsed'));
      if (savedTeams !== null) {
        setTeamsCollapsed(savedTeams === 'true');
      } else {
        // Default: collapsed for buyer, expanded for others
        setTeamsCollapsed(role === 'buyer');
      }
      const savedRole = localStorage.getItem(userScopedKey('sfx_sidebar_role_section_collapsed'));
      setRoleSectionCollapsed(savedRole === 'true');
      const savedTest = localStorage.getItem('sfx_sidebar_test_accounts_collapsed');
      setTestAccountsCollapsed(savedTest === 'true');
    };
    window.addEventListener('sfx-role-change', handleRoleChange);
    return () => window.removeEventListener('sfx-role-change', handleRoleChange);
  }, []);

  // Admin notification urgency (site admin only). ONE fetch function, fetched
  // ONCE per mount / navigation by the effect below, refreshed by the 15 s
  // interval — which does NOT also fetch immediately (URGPOLL round 1,
  // 2026-09-26: mount was 2 identical calls). No department dependency: a
  // site admin holds no assignment, so `activeDepartment` is always null here.
  const fetchAdminUrgency = useCallback(() => {
    apiFetch('/api/notifications/admin/urgency')
      .then(res => res.json())
      .then(data => {
        setAdminNotifCount(data.count || 0);
        setAdminNotifColor(data.color || 'green');
        setAdminUrgency({ red: data.red || 0, yellow: data.yellow || 0, green: data.green || 0 });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setAdminNotifCount(0);
      setAdminNotifColor('red');
      setAdminUrgency({ red: 0, yellow: 0, green: 0 });
      return undefined;
    }
    const interval = setInterval(fetchAdminUrgency, 15000);
    return () => clearInterval(interval);
  }, [isAdmin, fetchAdminUrgency]);

  // Fetch on mount and on navigation (only if admin).
  useEffect(() => {
    if (!isAdmin) return;
    fetchAdminUrgency();
  }, [location.pathname, isAdmin, fetchAdminUrgency]);

  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * 🚨 THE FIVE BADGE FETCHES BELOW HAND-WROTE THEIR OWN HEADERS UNTIL
   *    2026-08-12, AND EVERY ONE OF THE THREE WAS WRONG. THEY NOW PASS NONE.
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Each carried:
   *
   *     headers: { 'x-user-name': username, 'x-user-role': userRole,
   *                'x-department-id': activeDepartment?.id || '' }
   *
   * `x-department-id` — AN ASSIGNMENT ROW HAS NO `id`. `activeDepartment` is
   * always an element of the array `GET /api/roles/my-assignments` returns
   * (`contexts/DepartmentContext.js` sets it nowhere else), and that route
   * selects `da.department_id` — never `da.id`. So the expression was
   * `undefined || ''` and the header was ALWAYS the empty string. That is not
   * inert: `utils/api.js` merges caller headers LAST
   * (`{ ...autoHeaders, ...options.headers }`), so `''` OVERRODE the correct
   * `x-department-id` the API layer had just read from
   * `localStorage.sfx_active_department_id`.
   *
   * 🔑 CALLER-WINS IS DELIBERATE AND WAS NOT WEAKENED TO FIX THIS. It is what
   *    lets a call site legitimately scope a request to a department other than
   *    the active one. The callers were fixed instead, and
   *    `Layout.badgeDepartmentScope.test.js` §4 pins the merge order so nobody
   *    "fixes" the next instance of this in `api.js`.
   *
   * 🕶️ HONEST BLAST RADIUS, MEASURED RATHER THAN ASSUMED — this was a LATENT
   *    TRAP, not a live leak, and overstating it is how it gets fixed in the
   *    wrong place. `departmentScope` tests `if (deptIdHeader)` and `''` is
   *    falsy, so the header was IGNORED and the middleware's own ordered
   *    default applied — an empty header is exactly equivalent to omitting it,
   *    never broader. More decisively, at the time NEITHER endpoint these five
   *    hit read `req.activeDepartment` at all: `/api/notifications/
   *    urgency-summary` and `/api/hires/urgency-summary` both scoped on
   *    `req.userDepartments`. So the value had no observable effect on either
   *    response. What it WOULD do is silently answer for the wrong department
   *    the day either endpoint starts resolving the active one.
   *    ⚠️ THAT DAY CAME (2026-08-14): `/api/hires/urgency-summary`'s deptHires
   *    bucket now scopes on `req.activeDepartment` (hires buildDeptFilter,
   *    sealed-box ring-fence). Safe here BECAUSE this cleanup happened — every
   *    call below goes through bare `apiFetch`, which sends the real active
   *    department, and the effects re-run on the active department's id
   *    (`activeDepartmentId` since URGPOLL, 2026-09-26). Had the
   *    hand-written `''` header survived, this is the exact silent-wrong-
   *    department failure the sentence above predicted.
   *
   * `x-user-name` / `x-user-role` — FORBIDDEN OUTRIGHT. Root CLAUDE.md:
   * *"`apiFetch` … adds exactly ONE header — `x-department-id` … Never re-add
   * an identity header to make something work."* They did nothing:
   * `departmentScope` consults the header-identity shim only when
   * `!req.authenticatedViaSession`, so a real browser session always wins, and
   * the shim is env-gated to `test`/`development` regardless. Their only effect
   * was to make the call site look like it was authenticating itself — which is
   * how a wrong department key survived review inside the same object literal.
   *
   * ⚠️ The `sfx_requested_by` guard is KEPT. It no longer feeds a header, but it
   *    still gates WHEN these effects fire, and removing it would start issuing
   *    badge requests for sessions that previously issued none — a behaviour
   *    change unrelated to this fix.
   *
   * 🔢 ELEVEN SITES WERE CLEANED, NOT FIVE, AND THE EXTRA SIX ARE WHY THE
   *    ENUMERATION MATTERED MORE THAN THE FIX. A task brief named the five
   *    carrying the broken `x-department-id`; grepping the HEADER NAMES rather
   *    than the department expression found six more — three on
   *    `/api/approvals/pending/count` and three MORE on
   *    `/api/notifications/urgency-summary` — each with
   *    `{ 'x-user-name': userName, 'x-user-role': userRole }` and no department
   *    header at all. Those six are the same forbidden-identity-header
   *    violation and were removed in the same change; had they been left, this
   *    file would have gone on hand-writing identity headers onto the very
   *    endpoint the other five had just stopped hand-writing them onto.
   *    **Grep the header names, not the expression you were sent to fix.**
   *
   * 🔒 There are now ZERO hand-written `headers` objects in this file, which is
   *    what `Layout.badgeDepartmentScope.test.js` §3 asserts — over every
   *    request the component issues, not a chosen subset.
   */

  /**
   * ── THE TWO SIDEBAR URGENCY ENDPOINTS: ONE FETCH EACH, ONE REQUEST PER
   *    TRIGGER (URGPOLL, 2026-09-26) ─────────────────────────────────────────
   *
   * Measured on staging before this change: one Dept Orders load fired
   * `GET /api/notifications/urgency-summary` ~6 times in ~2 s, and one
   * session's hour held 142 of them against ~70 for every other badge
   * endpoint. FOUR effects fetched that one endpoint into TWO states — the
   * order dots' `urgencySummary` (a mount+30 s effect and a navigation effect)
   * and the To Approve badge's `approvalUrgency` (a mount+15 s effect and a
   * navigation effect, further down) — so mount was 4 identical calls and a
   * steady 30 s was 3. The dots' pair also keyed on the `activeDepartment`
   * OBJECT, which `DepartmentContext#fetchAssignments` re-creates on every
   * assignments refetch with the same `department_id`, so they re-fired on
   * renders where nothing had changed. `/api/hires/urgency-summary` had the
   * same mount+poll / navigation double.
   *
   * 🔑 THE SHAPE NOW, and each half is pinned by
   * `Layout.urgencyPollDedupe.test.js`:
   *   • ONE fetch function per endpoint. `fetchOrdersUrgency` sets BOTH
   *     states from the one response — `approvalUrgency` only while the
   *     caller holds an approval role (read through a ref AT RESPONSE TIME, so
   *     a role lost mid-flight cannot paint a badge the reset effect below has
   *     just cleared).
   *   • ONE trigger effect fetches both, once, on mount / navigation / role
   *     change / department change — keyed on `activeDepartmentId`, NEVER the
   *     object (see the 🔑 note in the To Approve docblock below).
   *   • ONE interval at the shared `POLL_INTERVAL_MS`, which does NOT also
   *     fetch immediately — the trigger effect already covers mount, and an
   *     immediate fetch here is exactly the duplicate this replaced.
   *   • The event-driven refetches (`hires-changed` on the dataBus,
   *     `sfx-approval-change` below) call the same functions.
   *
   * 🪤 NO IN-FLIGHT GUARD, DELIBERATELY. `apiFetch` reads the department from
   *    localStorage at CALL time, so a guard that dropped a request because
   *    one was already in flight would, on a department switch, drop the NEW
   *    department's request and let the OLD department's answer paint. The
   *    dedupe above removes the duplicates at their source instead. (Two
   *    in-flight requests can still resolve out of order across a switch —
   *    pre-existing, unchanged, and the next trigger corrects it.)
   *
   * ⚠️ The one visible change: the To Approve badge's COLOUR/tooltip now
   *    refreshes every 30 s instead of 15 s. Its NUMBER
   *    (`/api/approvals/pending/count`) keeps its own 15 s poll, unchanged.
   */
  const hasApprovalRole = isAdmin || APPROVAL_ROLES.includes(userRole);
  const hasApprovalRoleRef = useRef(hasApprovalRole);
  hasApprovalRoleRef.current = hasApprovalRole;
  const activeDepartmentId = activeDepartment?.department_id;

  const fetchOrdersUrgency = useCallback(() => {
    if (!localStorage.getItem('sfx_requested_by')) return;
    apiFetch('/api/notifications/urgency-summary')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) return;
        setUrgencySummary({
          myOrders: data.myOrders || { count: 0, color: 'green', red: 0, amber: 0 },
          deptOrders: data.deptOrders || { count: 0, color: 'green', red: 0, amber: 0 },
          waitingByDepartment: data.waitingByDepartment || {}
        });
        if (hasApprovalRoleRef.current && data.approvals) {
          setApprovalUrgency({
            color: data.approvals.color || 'green',
            red: data.approvals.red || 0,
            yellow: data.approvals.yellow || 0
          });
        }
      })
      .catch(() => {});
  }, []);

  const fetchHireUrgency = useCallback(() => {
    if (!localStorage.getItem('sfx_requested_by')) return;
    apiFetch('/api/hires/urgency-summary')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setHireUrgency({
            myHires: data.myHires || { count: 0, color: null },
            deptHires: data.deptHires || { count: 0, color: null }
          });
        }
      })
      .catch(() => {});
  }, []);

  // Fetch both, ONCE, on mount / navigation / role change / department change.
  useEffect(() => {
    fetchOrdersUrgency();
    fetchHireUrgency();
  }, [fetchOrdersUrgency, fetchHireUrgency, location.pathname, userRole, hasApprovalRole, activeDepartmentId]);

  // Refresh both on ONE interval. NO immediate fetch here (the effect above
  // owns that). It keeps the department dependency under the rule at the
  // `sfx-approval-change` listener below — it OWNS a timer — so a switch
  // re-arms the phase rather than letting a tick land moments after the
  // switch's own fetch.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrdersUrgency();
      fetchHireUrgency();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchOrdersUrgency, fetchHireUrgency, userRole, activeDepartmentId]);

  // Re-fetch hire urgency when another tab edits a hire (cross-tab dataBus).
  // Lets the sidebar dots update without waiting for the polling interval.
  // A subscription whose callback fetches: no department dependency (the rule
  // at the `sfx-approval-change` listener below).
  useEffect(() => subscribeDataBus('hires-changed', fetchHireUrgency), [fetchHireUrgency]);

  // Fetch productions assigned to the current user
  useEffect(() => {
    const userId = localStorage.getItem('sfx_user_id');
    if (!userId) {
      setUserProductions([]);
      return;
    }
    apiFetch(`/api/dashboard/user-productions?user_id=${encodeURIComponent(userId)}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setUserProductions(data);
        }
      })
      .catch(() => setUserProductions([]));
  }, [activeAccountId]);

  /**
   * ── THE TO APPROVE BADGE — AND IT IS TWO SERVER READS, NOT ONE ──────────
   *
   * `pendingApprovalCount` (the NUMBER) comes from
   * `GET /api/approvals/pending/count`; `approvalUrgency` (the COLOUR and the
   * `N critical, N moderate` tooltip) comes from the `approvals` bucket of
   * `GET /api/notifications/urgency-summary`. Both are rendered by the same
   * `<span>`, at three places in this file. **Anything that changes what one of
   * them counts has to change the other in the same commit**, or the badge goes
   * red over a queue with nothing in it.
   *
   * 🔀 **BOTH ENDPOINTS BECAME DEPARTMENT-SCOPED ON 2026-08-12, AND THAT IS WHY
   * `activeDepartment` IS NOW A DEPENDENCY OF THE TWO EFFECTS BELOW.** Product
   * owner: *"approvals badge doesn't refresh when you switch department"*.
   *
   * 🕶️ **HIS REPORT DESCRIBES THE SYMPTOM AND NOT THE CAUSE, AND THE
   * DISTINCTION DECIDES WHAT "FIXED" MEANS HERE.** The badge was not stale — it
   * was UNSCOPED. The number did not move on a department switch because it had
   * never been about the department: the endpoint selected purely on
   * `approver_user_id`. So the scoping is the fix, and this dependency array is
   * what stops the scoping from CREATING a genuine staleness bug in its place.
   * **Neither half is the fix on its own**, and applying only this one would
   * have made a correct-but-unscoped badge into a re-fetched-but-still-unscoped
   * badge — the same wrong number, arriving faster.
   *
   * 🪤 **THE DEPENDENCY WAS MISSING HERE AND PRESENT ON THE SIBLING EFFECTS,
   * WHICH IS WHAT MADE IT LOOK DELIBERATE.** The five above (`:835`, `:854`,
   * `:878`, `:897`, `:919` — the orders and hires urgency dots) all carry
   * `activeDepartment`; these did not.
   *
   * 🚨 **BUT DO NOT READ THOSE FIVE AS THE CORRECT PATTERN — an audit on
   * 2026-08-12 found they were the OPPOSITE mismatch at the time, and an
   * earlier draft of this very comment held them up as the good example.** As
   * of that audit, not one of the endpoints they called was scoped to the
   * ACTIVE department: `/api/hires/urgency-summary`'s `myHires` was
   * `o.user_id = ?` and its `deptHires` was a union over **every** department
   * the caller belonged to (`routes/hires.js`), and
   * `/api/notifications/urgency-summary`'s `myOrders` and `deptOrders` buckets
   * were the same two shapes. So each of those effects re-fetched a
   * byte-identical answer on every switch, and the `deptOrders` dot quietly
   * counted across departments while the page it labels does not.
   * **Reported, not fixed there — out of that change's blast radius**, and a
   * separate audit owned that question.
   *
   * ⚠️ **CORRECTED 2026-08-18 — AND THAT CORRECTION WAS ITSELF WRONG, FIXED
   * 2026-08-30 (Task 3, "a nav badge must match the page it labels").** The
   * paragraph above asserted the cross-department union as CURRENT fact for
   * two years' worth of readers, and it already misled one: an agent
   * re-reported this as a LIVE cross-tenant leak, having trusted the comment
   * over the code. Owner ruling 2026-08-15 — *"normal users can not see all
   * hires. Only their hires"* — DID close a real leak (bare MEMBERSHIP no
   * longer admitted a plain `user` to the dept-wide view), but this
   * paragraph then overstated what shipped with it: it said the loop was
   * narrowed "to the caller's ACTIVE department". **That was false, and had
   * been since it was written.** `routes/notifications.js`'s
   * `/urgency-summary` narrowed its `req.userDepartments` loop BY
   * CAPABILITY — dropping a department held by bare membership without
   * `canViewDepartmentOrders` — but the loop still ran over EVERY department
   * where the caller held the capability, whichever was ACTIVE. A caller
   * standing in department A who also held the capability in department B
   * still saw B's orders folded into the `deptOrders` badge — badge vs page
   * disagreeing exactly as the bullet two paragraphs above describes, alive
   * from 2026-08-15 to 2026-08-30 under a comment asserting it fixed.
   *
   * **What is true of the tree now:** `routes/notifications.js`'s
   * `resolveDeptOrdersViewIds` resolves the SAME per-department set
   * `GET /api/orders/department` gates on — the active department alone in
   * single mode (`canViewDepartmentOrders` checked EXPLICITLY, because
   * `resolveViewDepartmentIds`'s single-mode arm applies no capability
   * filtering of its own) and `resolveViewDepartmentIds(req,
   * 'canViewDepartmentOrders')`'s already-capability-checked set in All
   * mode — and feeds it to the `deptOrders` bucket ALONE. **There is no
   * `deptHires` bucket in that file** — a second, drifted hire-urgency
   * implementation of that name was DELETED from it on 2026-08-18 (see the
   * deletion note at that site in `routes/notifications.js`); an earlier
   * draft of this paragraph said the loop fed "both `deptOrders` and
   * `deptHires`", which was never true of the merged code.
   * `routes/hires.js`'s `/urgency-summary` gates its OWN `deptHires` bucket
   * via `mayViewDeptHires` + `buildDeptFilter` — also now MODE-AWARE
   * (`resolveViewDepartmentIds`, Task 2, 2026-08-30), not the bare
   * `req.activeDepartment` read this paragraph named until this correction.
   * A plain `user` who holds `canViewDepartmentOrders` nowhere still gets a
   * neutral (zero) dept badge on both endpoints, matching the 403/empty
   * answer they get on the lists themselves. **The rule two paragraphs
   * below still stands and is what caught the original gap**; it is also
   * why `activeDepartment` staying a dependency here is still correct now
   * that the endpoints agree with it, rather than merely re-fetching a
   * stale union faster.
   *
   * 🔑 **THE RULE, WHICH IS WHAT THIS ENTRY IS REALLY FOR: ASK BOTH QUESTIONS
   * TOGETHER — "does the endpoint scope by department?" and "does the effect
   * re-run when it changes?" — AND THEY MUST GIVE THE SAME ANSWER.** Either
   * mismatch is a defect and they look nothing alike: scoped-without-dependency
   * is a stale number after a switch (what the owner reported), while
   * unscoped-with-dependency is a wasted request hiding a ringfence the user
   * believes is being applied. **A missing dependency and a department-blind
   * endpoint are indistinguishable from inside this file**, which is why the
   * pairing has to be checked against the handler and never inferred from the
   * effects next door.
   *
   * 🔀 URGPOLL (2026-09-26): the COLOUR is no longer fetched by the effects
   * below — `approvalUrgency` is set by the shared `fetchOrdersUrgency` above,
   * from the same `urgency-summary` response as the order dots, and its
   * department dependency lives on that shared trigger effect. The two effects
   * below now fetch only the NUMBER (`pending/count`, still every 15 s). The
   * pairing rule above is unchanged: the NUMBER and the COLOUR still re-run on
   * the same department change, just from two effects instead of one.
   *
   * 🔑 The dep is `activeDepartmentId` (`activeDepartment?.department_id`),
   * never the object: the context hands out a fresh assignment object on every
   * `assignments` refetch, so depending on the object tears down and re-arms
   * the intervals and re-fetches on renders where nothing changed. The order
   * dots' effects DID key on the object until URGPOLL — pinned now by §4 of
   * `Layout.urgencyPollDedupe.test.js`.
   *
   * 🟢 The request itself was ALWAYS correct — `utils/api.js`'s `getAutoHeaders`
   * reads `sfx_active_department_id` from localStorage at CALL time, and
   * `switchDepartment` writes that key synchronously before it sets state. So
   * only the TIMING was ever wrong, which is exactly why the 15s poll masked it
   * into "the badge takes a moment to catch up" rather than a hard bug.
   */
  // `hasApprovalRole` is declared above, beside `fetchOrdersUrgency`, which
  // reads it (URGPOLL). The badge COLOUR (`approvalUrgency`) is set from that
  // shared fetch; only the NUMBER is fetched here, by ONE function.
  const fetchPendingCount = useCallback(() => {
    if (!localStorage.getItem('sfx_requested_by')) return;
    apiFetch('/api/approvals/pending/count')
      .then(res => res.json())
      .then(data => setPendingApprovalCount(data.count || 0))
      .catch(() => {});
  }, []);

  // The 15 s interval, and the reset for a caller without an approval role.
  // NO immediate fetch here (URGPOLL round 1, 2026-09-26: this and the effect
  // below both fetched on mount and on every department change — 2 per
  // trigger). It keeps the department dependency because it OWNS a timer
  // (the rule at the `sfx-approval-change` listener below), so a switch
  // re-arms the phase.
  useEffect(() => {
    if (!hasApprovalRole) {
      setPendingApprovalCount(0);
      setApprovalUrgency({ color: 'green', red: 0, yellow: 0 });
      return undefined;
    }
    const interval = setInterval(fetchPendingCount, 15000);
    return () => clearInterval(interval);
  }, [hasApprovalRole, userRole, activeDepartmentId, fetchPendingCount]);

  // Fetch the pending approval count ONCE on mount / navigation / role change /
  // department change. (The COLOUR's refetch is the shared trigger effect above.)
  //
  // ⚠️ A DEPARTMENT SWITCH IS NOT A NAVIGATION, so `location.pathname` does not
  // cover it — the switcher lives in this same chrome and changes no route. This
  // effect needs the department dependency in its own right: since URGPOLL
  // round 1 it is the ONLY thing that fetches on a switch (the interval effect
  // above re-arms its timer and fetches nothing).
  useEffect(() => {
    if (!hasApprovalRole) return;
    fetchPendingCount();
  }, [location.pathname, hasApprovalRole, userRole, activeDepartmentId, fetchPendingCount]);

  // Listen for approval changes from the Approvals page.
  //
  // 🔴 THIS ONE DELIBERATELY TAKES NO `activeDepartment` DEPENDENCY, and the
  // asymmetry with the two effects above is the point rather than an omission.
  // It registers a listener; it does not fetch. The fetch happens inside the
  // handler, at event time, and `apiFetch` reads the active department from
  // localStorage then — so the request is already scoped to whatever department
  // the user is standing in when the event fires. Adding the dep would only tear
  // down and re-add a `window` listener on every switch. **The rule is that an
  // effect needs the dependency when it OWNS a fetch or a timer, not when it
  // owns a subscription whose callback fetches.**
  useEffect(() => {
    const handleApprovalChange = () => {
      if (!hasApprovalRole) return;
      const userName = localStorage.getItem('sfx_requested_by');
      if (!userName) return;
      fetchPendingCount();
      // The shared fetch: it also refreshes the order dots from the same
      // response, which is the same server read the colour always came from.
      fetchOrdersUrgency();
    };
    window.addEventListener('sfx-approval-change', handleApprovalChange);
    return () => window.removeEventListener('sfx-approval-change', handleApprovalChange);
  }, [hasApprovalRole, userRole, fetchOrdersUrgency, fetchPendingCount]);

  // Apply dark mode class to <html> element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('sfx_dark_mode', darkMode.toString());
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const toggleMyOrdersCollapsed = () => {
    setMyOrdersCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(userScopedKey('sfx_sidebar_my_orders_collapsed'), String(next));
      return next;
    });
  };

  const toggleRoleSectionCollapsed = () => {
    setRoleSectionCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(userScopedKey('sfx_sidebar_role_section_collapsed'), String(next));
      return next;
    });
  };

  const toggleMarketplaceCollapsed = () => {
    setMarketplaceCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sfx_sidebar_marketplace_collapsed', String(next));
      return next;
    });
  };

  const toggleRiskAssessmentsCollapsed = () => {
    setRiskAssessmentsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sfx_sidebar_ra_collapsed', String(next));
      return next;
    });
  };

  const toggleTeamsCollapsed = () => {
    setTeamsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(userScopedKey('sfx_sidebar_teams_collapsed'), String(next));
      return next;
    });
  };

  const toggleTestAccountsCollapsed = () => {
    setTestAccountsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sfx_sidebar_test_accounts_collapsed', String(next));
      return next;
    });
  };

  // Build nav items based on activeRole from department context
  /**
   * 🔻 **RENAMED "To Approve" → "My Approvals", 2026-09-12 — PRODUCT-OWNER
   * RULING, AND IT IS A LABEL CHANGE AND NOTHING ELSE.** *"Keep 'To Approve' in
   * the right place but change it to My Approvals."* Same section, same
   * position, same `path`, same badge.
   *
   * 🔑 **THE REASON IS A COLLISION, NOT A PREFERENCE.** Department Orders grew a
   * queue TAB called **"To approve"** hours earlier, and the two lists are
   * different: the TAB is every `submitted` order in the DEPARTMENT, whoever is
   * named on the pending step; this PAGE is only the steps assigned to the
   * VIEWER (`GET /api/approvals/pending` is keyed on user id). Two screens, one
   * name, two different counts. "My Approvals" is distinct AND true, and it is
   * the naming the rest of this list already uses — My Orders, My Hires, My
   * Dashboard, My Settings.
   *
   * 🪤 **The `path` is deliberately untouched.** Renaming the route would break
   * every bookmark and deep link and would look identical in the diff.
   * ⚠️ **Rename this label and you MUST rename the `mobileLabels` key below** —
   * that map is keyed BY THIS STRING, so a half-done rename silently stops
   * matching and the bottom nav falls back to the full label.
   * Pinned by `Layout.approvalsNaming.test.js`, which asserts the nav label and
   * the Dept Orders tab label TOGETHER — either alone stays green while the
   * confusion returns.
   */
  const approvalsNavItem = { path: '/approvals', label: 'My Approvals', icon: ClipboardCheckIcon };
  const departmentOrdersNavItem = { path: '/department/orders', label: 'Dept Orders', icon: ClipboardListIcon };
  const departmentHiresNavItem = { path: '/department/hires', label: 'Dept Hires', icon: HiresIcon };
  const departmentDashNavItem = { path: '/department/dashboard', label: 'Department Dashboard', icon: ChartBarIcon };

  // Determine effective role for navigation: context activeRole, or localStorage fallback
  const effectiveNavRole = isAdmin ? 'admin' : (isBuyer ? 'buyer' : (activeRole || userRole));
  const hasDepartmentRole = DEPARTMENT_ROLES.includes(effectiveNavRole) || effectiveNavRole === 'buyer' || isBuyer;
  const hasApprovalRoleForNav = APPROVAL_ROLES.includes(effectiveNavRole) || isAdmin || isBuyer;

  // My Space (personal workspace) hides every role-specific item — Approvals,
  // Dept Orders/Hires/Dashboard, Budgeting, Admin Settings, etc. Personal nav
  // and Teams items remain (Teams pages already scope by active production).
  const isMySpace = !!activeDepartment?.production_is_personal;

  // buildNavItems: always returns only personal items (My Orders section).
  // Site admin cannot create orders directly (must impersonate), so the
  // "+ New Order" CTA is hidden from their sidebar.
  const buildNavItems = () => {
    if (isAdmin) return userNavItems.filter((item) => item.path !== '/orders/new');
    return [...userNavItems];
  };

  // buildRoleNavItems: returns role-specific items for the second sidebar section
  const budgetNavItem = { path: '/budget/overview', label: 'Budgeting', icon: BudgetIcon };
  // Stores capability points at the same Department Orders screen (delivery
  // queue), labelled "Deliveries" to make the goods-in purpose clear.
  const deliveriesNavItem = { path: '/department/orders', label: 'Deliveries', icon: ClipboardListIcon };
  // Does the caller hold department-settings authority in the department they
  // are STANDING IN? Gate on the CAPABILITY, never on `activeRole ===
  // 'supervisor'`: a role name is an any-department test wearing a department's
  // clothes, and it would exclude a future Coordinator role granted the same
  // column. Must agree with BuyerRoute in App.js — the nav is cosmetic and the
  // route guard is the gate, but a disagreement means either a link that
  // bounces or access with no way to reach it.
  const canManageDeptSettings = !!capabilities?.canManageDepartmentSettings;
  // MUST AGREE WITH `BuyerRoute` IN App.js — read the long comment there before
  // changing either. A disagreement means a link that bounces, or (which is what
  // happened on 2026-08-10) access with no way to reach it.
  //
  // 🔑 THIS DECIDES WHETHER A LINK IS DRAWN AND NOTHING ELSE. The two wider
  // operands are not an any-department authority grant: every request behind the
  // link is still resolved per department by the server, and every control on
  // the page renders from a server-computed answer for the row it acts on.
  const canReachAdminSettings = canManageDeptSettings
    || holdsDepartmentSettingsSomewhere || ownsProductionLabel;
  const buildRoleNavItems = () => {
    let items;
    if (effectiveNavRole === 'buyer' || isBuyer) {
      // Budgeting moved to the Planning section; re-added there with preserved gating.
      items = [approvalsNavItem, departmentOrdersNavItem, departmentHiresNavItem, departmentDashNavItem, adminSettingsNavItem];
    } else if (effectiveNavRole === 'supervisor') {
      items = [approvalsNavItem, departmentOrdersNavItem, departmentHiresNavItem, departmentDashNavItem];
    } else if (effectiveNavRole === 'sub_department_lead') {
      items = [approvalsNavItem, { path: '/department/orders', label: 'Sub Dep Orders', icon: ClipboardListIcon }, departmentHiresNavItem];
    } else {
      items = [];
    }
    // Additive Stores capability: surface the delivery queue if the base role
    // wouldn't already show a Department Orders entry.
    if (canManageStores && !items.some(i => i.path === '/department/orders')) {
      items = [deliveriesNavItem, ...items];
    }
    // Additive, and deliberately OUTSIDE the role branches: the people this
    // reaches include a creator whose only seat is `user` or
    // `sub_department_lead`, for whom every branch above yields nothing. Adding
    // it per-branch is how it went missing in the first place.
    if (canReachAdminSettings && !items.some(i => i.path === adminSettingsNavItem.path)) {
      items = [...items, adminSettingsNavItem];
    }
    return items;
  };

  // Compute section label for role-specific nav section
  const getRoleSectionLabel = () => {
    if (isAdmin) return 'Admin/Buyer';
    if (effectiveNavRole === 'buyer' || isBuyer) return 'Buyer';
    if (effectiveNavRole === 'supervisor') return 'Supervisor';
    if (effectiveNavRole === 'sub_department_lead') return 'Sub-Dept Lead';
    if (canManageStores) return 'Stores';
    // The section is rendered only when this returns a label AND there are items
    // (see the sidebar below), so a `user`-seated caller who can reach Admin
    // Settings needs one or their single nav item is built and then never drawn.
    if (canReachAdminSettings) return 'Settings';
    return null;
  };

  // For admin users, put Approvals in Admin/Buyer section.
  // Budgeting (moved to Planning) is excluded here and re-added there with preserved gating.
  const buildAdminNavItems = () => {
    let items = adminNavItems.filter((i) => i.path !== '/budget/overview');
    if (isAdmin) {
      // Insert Approvals at the beginning of admin section
      items.unshift(approvalsNavItem);
    }
    return items;
  };

  // Budgeting nests under the Planning section, gated to exactly the roles that
  // saw it before (admin/buyer always; others only when canViewBudget). Hidden
  // in My Space, matching the old behaviour where role items were suppressed.
  const canSeeBudget = !isMySpace && (isAdmin || isBuyer || effectiveNavRole === 'buyer' || canViewBudget);
  // Timesheets: every member of a REAL department (not My Space — no crew there;
  // not an unseated site admin — no active department for the routes to scope to).
  // 🔒 And only where the server says timesheets are ON — DEMO departments until launch
  // (owner 2026-10-28; server/helpers/timesheetsFeature.js sets `timesheets_enabled` on the seat).
  const canSeeTimesheets = !isMySpace && !!activeDepartment?.department_id && activeDepartment?.timesheets_enabled === true;
  const planningNavItems = [
    ...teamsNavItems,
    ...(canSeeTimesheets ? [timesheetsNavItem] : []),
    ...(canSeeBudget ? [budgetNavItem] : []),
  ];

  const roleNavItems = isMySpace ? [] : (isAdmin ? buildAdminNavItems() : buildRoleNavItems());
  const allNavItems = [...buildNavItems(), ...roleNavItems, ...planningNavItems, { path: '/hireforge', label: 'Browse Equipment', icon: MarketplaceSearchIcon }, gradingNavItem];

  // A short label too long for the 58px slot that must not be ellipsed WRAPS
  // onto two lines instead (2026-10-09, owner: "Sub Dept Orders", two lines).
  // Every other label keeps `truncate`.
  const MOBILE_TWO_LINE_LABELS = new Set(['Sub Dep Orders', 'My Approvals']);
  // Shorter mobile labels when all 6 nav items are shown (admin mode)
  const mobileLabels = {
    'New Order': 'New',
    'My Orders': 'My Orders',
    'My Hires': 'My Hires',
    'My Dashboard': 'My Dash',
    'Grading': 'Grading',
    'My Settings': 'Profile',
    // 🔻 2026-09-12: the KEY follows the nav label (this map is keyed by it), and
    // the SHORT FORM went `Approve` → `Approvals`. `Approve` is a VERB and reads as
    // the action — which is the confusion the rename exists to end; `Approvals`
    // is the noun, the same width class, and matches the sidebar.
    // 🔀 2026-10-09 (owner, demo run-through): back to "My Approvals" on the
    // phone; it wraps onto two lines if the 58px slot needs it.
    'My Approvals': 'My Approvals',
    'Dept Orders': 'Dep Orders',
    // 🔀 2026-10-09 (owner, demo run-through): "Sub Orders" → "Sub Dept Orders",
    // on TWO lines (his choice) — see `MOBILE_TWO_LINE_LABELS` below.
    'Sub Dep Orders': 'Sub Dept Orders',
    'Department Dashboard': 'Dep Dash',
    'Dept Hires': 'Dep Hires',
    'All Orders': 'All',
    'Admin Hires': 'AHires',
    'Admin Dashboard': 'Stats',
    'Admin Settings': 'Settings',
    'Budgeting': 'Budgeting',
    'Site Analytics': 'Analytics',
    'Browse Equipment': 'Hire Forge',
    'My Listings': 'Listings',
    'Manage Teams': 'Teams',
    'My Tasks': 'Tasks',
    'Add Listing': 'Add',
  };

  const navLinkClasses = ({ isActive }) =>
    `flex flex-col items-center justify-center px-0.5 sm:px-1 py-1 text-[9px] sm:text-[10px] leading-tight transition-colors duration-150 min-w-0 ${
      isActive
        ? 'text-indigo-700 dark:text-white font-semibold'
        : 'text-gray-500 hover:text-gray-700 dark:text-blue-200 dark:hover:text-white'
    }`;

  const sidebarLinkClasses = ({ isActive }) =>
    `flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-1.5 rounded-lg text-[13px] font-medium transition-colors duration-150 ${
      isActive
        ? 'bg-indigo-50 text-indigo-700 dark:bg-white/15 dark:text-white'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-blue-200 dark:hover:bg-white/10 dark:hover:text-white'
    }`;

  return (
    <NotificationFeedContext.Provider value={notificationFeed}>
    <div className={`min-h-screen bg-gray-100 flex flex-col lg:flex-row`}
      style={{
        '--sidebar-width': sidebarCollapsed ? '4rem' : '16rem',
        ...(showImpersonationBanner ? {
          paddingTop: `${impersonationBannerHeight ?? 40}px`,
          // Read by the bell's mobile popup, which is `fixed` below the header.
          '--impersonation-offset': `${impersonationBannerHeight ?? 40}px`,
        } : {}),
      }}
    >
      {/* Impersonation banner */}
      {/* 🔑 `pointer-events-none` on the bar, `pointer-events-auto` on its one
          control: the translucent bar is a LABEL, so a click anywhere but
          "Exit Impersonation" reaches whatever it tints (the sticky title bar
          and its bell, after scrolling). Pinned by
          Layout.impersonationBannerBell.test.js; the hit test itself is a
          browser measurement, recorded in that file. */}
      {showImpersonationBanner && (
        /* 📱 ONE LINE ON A PHONE, NEVER WIDER THAN THE SCREEN (2026-09-30). At
           360px it wrapped to 56px (99px at 130% Android text scaling), eating
           the header. The name truncates (`min-w-0` + `truncate`), the bar
           clips (`overflow-hidden`), and nothing in it has a min-content
           width that could widen the page — a wider page is what pushes
           Android Chrome's fixed bottom nav off the screen. */
        <div ref={impersonationBannerRef} data-testid="impersonation-banner" className="fixed top-0 left-0 right-0 z-[60] pointer-events-none overflow-hidden bg-amber-500/50 dark:bg-amber-600/50 text-white text-xs sm:text-sm font-medium text-center py-1 sm:py-2 px-2 sm:px-4 flex items-center justify-center gap-2 sm:gap-3 shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="min-w-0 truncate">Viewing as <strong>{authUser?.display_name}</strong></span>
          <button
            onClick={exitImpersonation}
            className="pointer-events-auto shrink-0 whitespace-nowrap sm:ml-2 px-3 py-1 min-h-[32px] bg-white/20 hover:bg-white/30 rounded text-xs font-bold transition-colors"
          >
            Exit Impersonation
          </button>
        </div>
      )}
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className={`hidden lg:flex lg:flex-col lg:min-h-screen bg-white dark:bg-[#1e3a5f] text-gray-800 dark:text-white shadow-lg border-r border-gray-200 dark:border-transparent transition-all duration-200 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className="px-3 py-3 border-b border-gray-200 dark:border-white/10">
          <h1
            className={`cursor-pointer hover:opacity-80 transition-opacity text-gray-500 dark:text-white uppercase whitespace-nowrap relative flex items-center justify-center ${sidebarCollapsed ? 'overflow-visible' : 'overflow-hidden'}`}
            style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: sidebarCollapsed ? '0.75rem' : '1.45rem', letterSpacing: '0.12em', padding: sidebarCollapsed ? '7px 4px' : '7px 28px' }}
            onClick={() => { if (sidebarCollapsed) { setSidebarCollapsed(false); localStorage.setItem('sfx_sidebar_collapsed', 'false'); } navigate(isAdmin ? '/admin/dashboard' : (activeRole === 'buyer' ? '/department/dashboard' : '/dashboard')); }}
            title="Go to Dashboard"
          >
            {!sidebarCollapsed && <>
              <span className="absolute top-0 left-0 w-3 h-3 border-gray-400 dark:border-white" style={{ borderTopWidth: '1px', borderLeftWidth: '1px', borderTopStyle: 'solid', borderLeftStyle: 'solid' }} />
              <span className="absolute top-0 right-0 w-3 h-3 border-gray-400 dark:border-white" style={{ borderTopWidth: '1px', borderRightWidth: '1px', borderTopStyle: 'solid', borderRightStyle: 'solid' }} />
              <span className="absolute bottom-0 left-0 w-3 h-3 border-gray-400 dark:border-white" style={{ borderBottomWidth: '1px', borderLeftWidth: '1px', borderBottomStyle: 'solid', borderLeftStyle: 'solid' }} />
              <span className="absolute bottom-0 right-0 w-3 h-3 border-gray-400 dark:border-white" style={{ borderBottomWidth: '1px', borderRightWidth: '1px', borderBottomStyle: 'solid', borderRightStyle: 'solid' }} />
              CREW FORGE
            </>}
            {sidebarCollapsed && <>
              <span className="absolute w-1.5 h-1.5 border-gray-400 dark:border-white" style={{ top: '-3px', left: '-3px', borderTopWidth: '0.5px', borderLeftWidth: '0.5px', borderTopStyle: 'solid', borderLeftStyle: 'solid' }} />
              <span className="absolute w-1.5 h-1.5 border-gray-400 dark:border-white" style={{ top: '-3px', right: '-3px', borderTopWidth: '0.5px', borderRightWidth: '0.5px', borderTopStyle: 'solid', borderRightStyle: 'solid' }} />
              <span className="absolute w-1.5 h-1.5 border-gray-400 dark:border-white" style={{ bottom: '-3px', left: '-3px', borderBottomWidth: '0.5px', borderLeftWidth: '0.5px', borderBottomStyle: 'solid', borderLeftStyle: 'solid' }} />
              <span className="absolute w-1.5 h-1.5 border-gray-400 dark:border-white" style={{ bottom: '-3px', right: '-3px', borderBottomWidth: '0.5px', borderRightWidth: '0.5px', borderBottomStyle: 'solid', borderRightStyle: 'solid' }} />
              CF
            </>}
          </h1>
        </div>

        {/* Production-Department Switcher + collapse toggle */}
        {!sidebarCollapsed ? (
          <div className="flex items-center relative z-30">
            <div className="flex-1 min-w-0"><ProductionDepartmentSwitcher waitingByDepartment={urgencySummary.waitingByDepartment} /></div>
            <button
              onClick={toggleSidebarCollapsed}
              className="px-2 py-3 text-gray-400 dark:text-blue-300/60 hover:text-gray-600 dark:hover:text-white transition-colors"
              title="Collapse sidebar"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            onClick={toggleSidebarCollapsed}
            className="w-full flex items-center justify-center py-2 mt-4 mb-[-8px] text-gray-400 dark:text-blue-300/60 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            title="Expand sidebar"
          >
            <svg className="w-4 h-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        )}

        <nav className="flex-1 py-4 px-3 space-y-1" aria-label="Main navigation">
          {!sidebarCollapsed && <button
            onClick={toggleMyOrdersCollapsed}
            className="w-full flex items-center justify-between px-4 py-1 mb-1 text-[11px] font-semibold uppercase tracking-widest cursor-pointer transition-colors text-gray-400 hover:text-gray-600 dark:text-blue-300/60 dark:hover:text-blue-200"
            data-testid="sidebar-my-orders-header"
            aria-expanded={!myOrdersCollapsed}
          >
            <span>My Orders</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform duration-200 ${myOrdersCollapsed ? '-rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>}
          {(!sidebarCollapsed ? !myOrdersCollapsed : true) && buildNavItems().map((item) => (
            <NavLink key={item.path} to={item.path} end className={sidebarLinkClasses} title={sidebarCollapsed ? item.label : undefined} onClick={() => { setMarketplaceCollapsed(true); localStorage.setItem('sfx_sidebar_marketplace_collapsed', 'true'); }}>
              <div className="relative">
                <item.icon />
                {/* No approval badge here: buildNavItems() only ever returns
                    userNavItems (/orders/new, /orders, /hires, /dashboard,
                    /settings), which never includes /approvals — a dead
                    approval-badge-sidebar render sat here until removed.
                    The reachable badge is approval-badge-admin-sidebar,
                    below, in the roleNavItems.map() loop. */}
                {item.path === '/orders' && (urgencySummary.myOrders.color === 'red' || urgencySummary.myOrders.color === 'amber') && (
                  <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${urgencySummary.myOrders.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="my-orders-urgency-dot-sidebar" title={tightLeadTimesTooltip(urgencySummary.myOrders.count)} />
                )}
                {item.path === '/hires' && (hireUrgency.myHires.color === 'red' || hireUrgency.myHires.color === 'amber') && (
                  <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${hireUrgency.myHires.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="my-hires-urgency-dot-sidebar" title={`${hireUrgency.myHires.count} hire${hireUrgency.myHires.count !== 1 ? 's' : ''} with near return dates`} />
                )}
              </div>
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}

          {getRoleSectionLabel() && roleNavItems.length > 0 && (
            <>
              {!sidebarCollapsed && <button
                onClick={toggleRoleSectionCollapsed}
                className="w-full flex items-center justify-between px-4 py-1 mt-4 mb-1 text-[11px] font-semibold uppercase tracking-widest cursor-pointer transition-colors text-gray-400 hover:text-gray-600 dark:text-blue-300/60 dark:hover:text-blue-200"
                data-testid="sidebar-role-section-header"
                aria-expanded={!roleSectionCollapsed}
              >
                <span>{getRoleSectionLabel()}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transition-transform duration-200 ${roleSectionCollapsed ? '-rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>}
              {sidebarCollapsed && <hr className="mx-2 my-2 border-0 border-t border-gray-200 dark:border-blue-300/40" />}
              {(!sidebarCollapsed ? !roleSectionCollapsed : true) && roleNavItems.map((item) => (
                <NavLink key={item.path} to={item.path} end className={sidebarLinkClasses} title={sidebarCollapsed ? item.label : undefined} onClick={() => { setMarketplaceCollapsed(true); localStorage.setItem('sfx_sidebar_marketplace_collapsed', 'true'); }}>
                  <div className="relative">
                    <item.icon />
                    {item.path === '/approvals' && pendingApprovalCount > 0 && (
                      <span className={`absolute -top-2 -right-2 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] shadow-lg ${
                        approvalUrgency.color === 'red' ? 'bg-red-500 dark:bg-red-600' : approvalUrgency.color === 'amber' ? 'bg-amber-500 dark:bg-amber-600' : 'bg-green-500 dark:bg-green-600'
                      }`} data-testid="approval-badge-admin-sidebar" title={`${approvalUrgency.red} critical, ${approvalUrgency.yellow} moderate`}>
                        {pendingApprovalCount > 99 ? '99+' : pendingApprovalCount}
                      </span>
                    )}
                    {item.path === '/department/orders' && adminNotifCount > 0 && (
                      <span className={`absolute -top-2 -right-2 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] shadow-lg ${
                        deptOrdersBadgeColour === 'red' ? 'bg-red-500' : (deptOrdersBadgeColour === 'yellow' || deptOrdersBadgeColour === 'amber') ? 'bg-yellow-500' : 'bg-green-500'
                      }`} data-testid="admin-notif-badge-sidebar" title={`${adminUrgency.red} urgent, ${adminUrgency.yellow} moderate, ${adminUrgency.green} low priority`}>
                        {adminNotifCount > 99 ? '99+' : adminNotifCount}
                      </span>
                    )}
                    {item.path === '/department/orders' && adminNotifCount === 0 && (urgencySummary.deptOrders.color === 'red' || urgencySummary.deptOrders.color === 'amber') && (
                      <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${urgencySummary.deptOrders.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="dept-orders-urgency-dot-sidebar" title={tightLeadTimesTooltip(urgencySummary.deptOrders.count)} />
                    )}
                    {(item.path === '/department/hires' || item.path === '/admin/hires') && (hireUrgency.deptHires.color === 'red' || hireUrgency.deptHires.color === 'amber') && (
                      <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${hireUrgency.deptHires.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="dept-hires-urgency-dot-sidebar" title={`${hireUrgency.deptHires.count} hire${hireUrgency.deptHires.count !== 1 ? 's' : ''} with near return dates`} />
                    )}
                  </div>
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </>
          )}

          {/* Planning section — visible to all users (was "Teams") */}
          {!sidebarCollapsed && <button
            onClick={toggleTeamsCollapsed}
            className="w-full flex items-center justify-between px-4 py-1 mt-4 mb-1 text-[11px] font-semibold uppercase tracking-widest cursor-pointer transition-colors text-gray-400 hover:text-gray-600 dark:text-blue-300/60 dark:hover:text-blue-200"
            data-testid="sidebar-teams-header"
            aria-expanded={!teamsCollapsed}
          >
            <span>Planning</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform duration-200 ${teamsCollapsed ? '-rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>}
          {sidebarCollapsed && <hr className="mx-2 my-2 border-0 border-t border-gray-200 dark:border-blue-300/40" />}
          {(!sidebarCollapsed ? !teamsCollapsed : true) && planningNavItems.map((item) => (
            <NavLink key={item.path} to={item.path} end className={sidebarLinkClasses} title={sidebarCollapsed ? item.label : undefined}>
              <item.icon />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}

          {/* Risk Assessments section — visible to all users including My Space.
              RAs are private to the creator (server-side filter), so showing the
              section in My Space lets individual users author their own RAs. */}
          {!sidebarCollapsed && <button
            onClick={toggleRiskAssessmentsCollapsed}
            className="w-full flex items-center justify-between px-4 py-1 mt-4 mb-1 text-[11px] font-semibold uppercase tracking-widest cursor-pointer transition-colors text-gray-400 hover:text-gray-600 dark:text-blue-300/60 dark:hover:text-blue-200"
            data-testid="sidebar-risk-assessments-header"
            aria-expanded={!riskAssessmentsCollapsed}
          >
            <span>Risk Assessments</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform duration-200 ${riskAssessmentsCollapsed ? '-rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>}
          {sidebarCollapsed && <hr className="mx-2 my-2 border-0 border-t border-gray-200 dark:border-blue-300/40" />}
          {(!sidebarCollapsed ? !riskAssessmentsCollapsed : true) && riskAssessmentNavItems.map((item) => (
            <NavLink key={item.path} to={item.path} end className={sidebarLinkClasses} title={sidebarCollapsed ? item.label : undefined}>
              <item.icon />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}

          {/* Marketplace section — visible to all users */}
          {!sidebarCollapsed && <button
            onClick={toggleMarketplaceCollapsed}
            className="w-full flex items-center justify-between px-4 py-1 mt-4 mb-1 text-[11px] font-semibold uppercase tracking-widest cursor-pointer transition-colors text-gray-400 hover:text-gray-600 dark:text-blue-300/60 dark:hover:text-blue-200"
            data-testid="sidebar-marketplace-header"
            aria-expanded={!marketplaceCollapsed}
          >
            <span>Hire Forge</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform duration-200 ${marketplaceCollapsed ? '-rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>}
          {sidebarCollapsed && <hr className="mx-2 my-2 border-0 border-t border-gray-200 dark:border-blue-300/40" />}
          {(!sidebarCollapsed ? !marketplaceCollapsed : true) && marketplaceNavItems.map((item) => (
            <NavLink key={item.path} to={item.path} end className={sidebarLinkClasses} title={sidebarCollapsed ? item.label : undefined} onClick={() => { setMyOrdersCollapsed(true); localStorage.setItem(userScopedKey('sfx_sidebar_my_orders_collapsed'), 'true'); setRoleSectionCollapsed(true); localStorage.setItem(userScopedKey('sfx_sidebar_role_section_collapsed'), 'true'); }}>
              <item.icon />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}

          {/* Grading — pinned to the very bottom of the sidebar, below all sections */}
          <NavLink
            to={gradingNavItem.path}
            end
            className={(state) => `${sidebarLinkClasses(state)} mt-4`}
            title={sidebarCollapsed ? gradingNavItem.label : undefined}
          >
            <GradingIcon />
            {!sidebarCollapsed && <span>{gradingNavItem.label}</span>}
          </NavLink>

          {!sidebarCollapsed && <>
          <hr className="mx-3 mt-16 border-0 border-t border-gray-200 dark:border-blue-300/40" />
          {/* User info + Logout */}
          <div className="px-3 py-2 mt-2">
            {authUser && (
              <div className="text-xs text-gray-500 dark:text-blue-300 mb-2 truncate" title={authUser.email}>
                {authUser.display_name}
              </div>
            )}
            <button
              onClick={async () => { await logout(); window.location.href = '/login'; }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-white/10 dark:text-blue-200 dark:hover:bg-white/20 dark:hover:text-white"
              data-testid="sidebar-logout"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>
          </>}
        </nav>

        {/* Collapse toggle button */}
        <button
          onClick={toggleSidebarCollapsed}
          className="flex items-center justify-end px-3 py-2 text-gray-400 dark:text-blue-300/60 hover:text-gray-600 dark:hover:text-white transition-colors border-t border-gray-200 dark:border-white/10"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg className={`w-4 h-4 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
          {!sidebarCollapsed && <span className="ml-2 text-xs">Collapse</span>}
        </button>

        {!sidebarCollapsed && (
          <div className="p-3 border-t border-gray-200 dark:border-white/10">
            <p className="text-xs text-gray-400 dark:text-blue-400" style={{ fontFamily: "'Orbitron', sans-serif" }}>CREW FORGE v2.0</p>
          </div>
        )}
      </aside>

      {/* Mobile Header - hidden on desktop */}
      <header className="lg:hidden bg-white dark:bg-[#1e3a5f] text-gray-800 dark:text-white px-2 sm:px-4 py-3 shadow-md border-b border-gray-200 dark:border-transparent">
        <div className="relative flex items-center justify-between gap-1">
          {/* Phone-only production name — a BUTTON since 2026-10-04 (owner: "how does someone on
              mobile switch productions?"): it opens the switcher row below, which is otherwise
              hidden under `sm`. Width stops short of the bell + theme controls so it never
              covers them. */}
          {activeProductionName && (
            <button
              type="button"
              data-dept-switcher-trigger=""
              data-testid="phone-production-switch"
              onClick={() => setPhoneSwitcherOpen((o) => !o)}
              aria-expanded={phoneSwitcherOpen}
              aria-label={`Switch production or department (current: ${activeProductionName}${activeSubDepartmentName ? `, ${activeSubDepartmentName}` : ''})`}
              className="sm:hidden absolute left-[44px] -bottom-1.5 py-1.5 max-w-[calc(100%-44px-96px)] inline-flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-white whitespace-nowrap"
              title={activeSubDepartmentName ? `${activeProductionName} (${activeSubDepartmentName})` : activeProductionName}
            >
              <span className="truncate min-w-0">{activeProductionName}</span>
              <span aria-hidden="true" className={`flex-shrink-0 text-[10px] text-gray-500 dark:text-blue-200 transition-transform ${phoneSwitcherOpen ? 'rotate-180' : ''}`}>&#9662;</span>
              {/* After the ▾ (owner 2026-10-09). `flex-shrink-0`: a long
                  production name truncates first, the sub-department stays. */}
              {activeSubDepartmentName && (
                <span className="flex-shrink-0 font-normal text-gray-500 dark:text-blue-200" data-testid="phone-sub-department">
                  ({activeSubDepartmentName})
                </span>
              )}
            </button>
          )}
          <div className="min-w-0 flex-shrink flex items-end gap-2">
            <h1
              className="cursor-pointer hover:opacity-80 transition-opacity text-gray-500 dark:text-white uppercase whitespace-nowrap relative flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:py-1 sm:px-4 flex-shrink-0"
              style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.1em' }}
              onClick={() => navigate(isAdmin ? '/admin/dashboard' : (activeRole === 'buyer' ? '/department/dashboard' : '/dashboard'))}
              title="Go to Dashboard"
            >
              <span className="absolute top-0 left-0 w-2 h-2 border-gray-400 dark:border-white" style={{ borderTopWidth: '1px', borderLeftWidth: '1px', borderTopStyle: 'solid', borderLeftStyle: 'solid' }} />
              <span className="absolute top-0 right-0 w-2 h-2 border-gray-400 dark:border-white" style={{ borderTopWidth: '1px', borderRightWidth: '1px', borderTopStyle: 'solid', borderRightStyle: 'solid' }} />
              <span className="absolute bottom-0 left-0 w-2 h-2 border-gray-400 dark:border-white" style={{ borderBottomWidth: '1px', borderLeftWidth: '1px', borderBottomStyle: 'solid', borderLeftStyle: 'solid' }} />
              <span className="absolute bottom-0 right-0 w-2 h-2 border-gray-400 dark:border-white" style={{ borderBottomWidth: '1px', borderRightWidth: '1px', borderBottomStyle: 'solid', borderRightStyle: 'solid' }} />
              <span className="sm:hidden">CF</span>
              <span className="hidden sm:inline">CREW FORGE</span>
            </h1>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <NotificationBell />
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 dark:bg-white/10 dark:hover:bg-white/20 dark:text-blue-200 dark:hover:text-white transition-colors"
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </div>
        {/* Mobile Production-Department Switcher — on a phone, shown only while the production
            name above has opened it; it hides again when its list closes. */}
        <div className={`${phoneSwitcherOpen ? 'block' : 'hidden'} sm:block bg-[#1a3350] border-t border-white/10`} data-testid="mobile-dept-switcher-row">
          <ProductionDepartmentSwitcher
            compact
            waitingByDepartment={urgencySummary.waitingByDepartment}
            forceOpen={phoneSwitcherOpen}
            onDropdownClosed={() => setPhoneSwitcherOpen(false)}
          />
        </div>
      </header>

      {/* Main Content */}
      {/* 🔑 `min-w-0` here, NOT `overflow-auto` — and the swap is the only reason
          `position: sticky` works anywhere in this app.

          WHAT WAS BROKEN. `overflow-auto` sat on this element from the AutoForge
          bootstrap (7212e27, 2026-03-26) and `git log -L` shows the line was never
          once edited, so it was generator boilerplate, not a fix for anything. It
          made <main> the nearest ancestor with a non-visible overflow, i.e. the
          scrollport that every `sticky` DESCENDANT positions itself against — the
          desktop title bar immediately below, and the page toolbars on Breakdown,
          Manage Teams and My Tasks. But the flex root above is `min-h-screen`, so
          <main> is never height-constrained: it is exactly as tall as its content,
          its scrollport therefore never scrolls, the document scrolls instead, and
          a sticky element whose scrollport never scrolls never sticks. It was
          positioned correctly and did nothing — app-wide, since day one. Measured
          in Chrome against the real compiled stylesheet, at scrollY 900: title bar
          top = -900, toolbar top = -820, `top: 56px` computed and inert.
          Nothing else in the chain contributes: no ancestor from a toolbar up to
          <html> carries a transform, filter, will-change, contain, perspective or
          backdrop-filter, and <main> was the only non-visible overflow.

          WHY IT IS NOT SIMPLY DELETED, which is the part that will be lost first.
          `overflow-auto` WAS load-bearing — just not for scrolling. A flex item
          whose overflow is not `visible` gets an automatic minimum size of ZERO,
          and that is what allowed <main> to shrink below the min-content width of
          the 1310px order table. Remove the utility bare and <main> measures
          1360px inside a 1024px column, so the PAGE gains a horizontal scrollbar:
          measured +200px at a 1280 viewport and +456px at 1024. That is the defect
          721d223 already had to fix once. `min-w-0` grants exactly that same
          shrink permission explicitly, without making <main> a scrollport, and
          unlike `overflow-auto` it establishes no block formatting context —
          geometry at rest is identical either way (main height, toolbar and footer
          offsets, document height all unchanged at 1280 and 500, measured).

          THE RESIDUAL TRADE-OFF, stated so nobody discovers it as a surprise.
          Content wider than <main> that has no scrollport of its own used to get a
          horizontal scrollbar on <main> — which, since <main> is as tall as the
          whole document, sat thousands of pixels below the viewport and was barely
          reachable anyway. Such content now reaches the page instead. Every wide
          surface in the app already carries its own scrollport (OrderTableShell's
          `overflow-x-auto`, all five Gantt/timeline views), so nothing measured
          regresses. If a new one ever overflows sideways, give THAT surface a
          scrollport — never put one back on <main>, or sticky dies everywhere
          again, silently, with every class still reading as correct.

          Pinned by client/src/components/Layout.stickyScrollport.test.js. */}
      <main className="flex-1 pb-20 lg:pb-0 min-w-0">
        <div className="hidden lg:flex items-center justify-between px-4 py-2 sticky top-0 z-30 bg-gray-100 dark:bg-gray-900">
          <NotificationBell />
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 flex-1 ml-4">
            {(() => {
              // Check dynamic routes first
              let title = '';
              if (titleOverride) {
                title = titleOverride;
              } else if (/^\/orders\/\d+\/edit/.test(location.pathname)) {
                title = 'Edit Order';
              } else if (/^\/orders\/\d+/.test(location.pathname)) {
                title = 'Order Detail';
              } else {
                // The viewer's OWN sidebar items come first, so the title is
                // the label they clicked: `/department/orders` reads "Sub Dep
                // Orders" / "Dept Orders" / "Deliveries" for its role, and
                // "All Orders" only for a site admin. Until 2026-10-08 the
                // admin list sat first and EVERY viewer's title read "All
                // Orders" (demo rehearsal). The sort below is stable, so the
                // earlier entry wins a same-length tie.
                // 🪤 The admin list also carried the ONLY title for two pages
                // non-admins reach — Budgeting and Admin Settings — so they are
                // listed here for everyone (review of 7e44c1a8: a buyer on
                // /budget/overview got an empty title).
                const allItems = [...roleNavItems, ...userNavItems, ...teamsNavItems, ...(isAdmin ? adminNavItems : []),
                  budgetNavItem, timesheetsNavItem, adminSettingsNavItem,
                  { path: '/department/orders', label: 'Dept Orders' },
                  { path: '/department/hires', label: 'Dept Hires' },
                  { path: '/department/dashboard', label: 'Dept Dashboard' },
                  { path: '/approvals', label: 'Approvals' },
                ];
                // Sort by descending path length so /teams/manage matches its
                // own entry before falling through to the shorter /teams entry.
                const sortedItems = [...allItems].sort((a, b) => b.path.length - a.path.length);
                const match = sortedItems.find(i => location.pathname === i.path || (i.path !== '/' && location.pathname.startsWith(i.path + '/')));
                title = match?.label || '';
              }
              const subtitle = activeDepartment
                ? spaceLabelFor(
                    {
                      is_personal: activeDepartment.production_is_personal,
                      owner_user_id: activeDepartment.production_owner_user_id,
                      production_name: activeDepartment.production_name,
                      company_name: activeDepartment.company_name,
                    },
                    authUser
                  )
                : (isAdmin ? 'All Productions' : '');
              return title ? (
                <>
                  {title}
                  {subtitle && (
                    <span className="font-normal text-sm text-gray-400 dark:text-gray-500 ml-2" data-testid="header-production-subtitle">
                      {subtitle}{activeSubDepartmentName ? ` (${activeSubDepartmentName})` : ''}
                    </span>
                  )}
                  {pageSummary && <span className="font-normal text-sm text-gray-400 dark:text-gray-500 ml-2">{pageSummary}</span>}
                </>
              ) : null;
            })()}
          </h2>
          {/* 🔻 THE GREETING IS GONE (2026-08-09) — the NAME is not, and the
              distinction is the whole change. Product owner: "I just noticed the
              welcome back message is on every apge so feel free to come up with
              another idea". You are not welcomed back to a page you are already
              on, so the pleasantry repeated on every route while saying nothing;
              the identity it carried is a different thing and stays.

              🔑 IT RECLAIMS NO VERTICAL SPACE, AND THAT WAS THE POINT OF
              CHECKING FIRST. The original request was to remove this to gain a
              row. It never could: this is ONE `hidden lg:flex` row that also
              carries NotificationBell, the page title + department subtitle +
              `pageSummary` count, and the dark-mode toggle — all of which
              survive — and its 56px height is set by the BELL (a 24px icon in
              `p-2` = 40px, + `py-2` = 56), not by this `text-sm` 20px span. So
              `APP_BAR_LG_PX = 56` and `ui/Toolbar`'s `top-0 lg:top-14` are
              deliberately UNCHANGED here. Do not "finish the job" by retuning
              either — see hooks/useStickyToolbar.js:6-7 for the derivation and
              Layout.stickyScrollport.test.js §3 for the Chrome measurement.

              ⚠️ WHY THE NAME SURVIVES RATHER THAN GOING WITH IT. It is not
              unconditionally shown elsewhere. The sidebar's copy (:1461-1465) is
              gated on `!sidebarCollapsed`, so it vanishes in the `lg:w-16`
              collapsed state — a persistent preference with its own
              `sfx_sidebar_collapsed` key — leaving this the ONLY thing naming
              the account for a collapsed-sidebar, non-impersonating user.
              🟢 Impersonation is NOT the reason it stays: that case is already
              carried, more loudly and at every breakpoint, by the fixed amber
              "Viewing as X" banner at :1209-1221, which this span never
              improved on. Deleting this line outright is a defensible product
              call and costs exactly the collapsed-sidebar case — but it is the
              owner's call, not a tidy-up.

              ♿ The visible text is the bare name, so the `sr-only` prefix
              supplies the context "Welcome back," used to give a screen reader.
              That is the components/InactiveBadge.js `title` + `sr-only`
              device, and it is why this is not simply a text deletion. */}
          <span
            className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-3"
            data-testid="app-bar-user"
            title={`Signed in as ${authUser?.display_name || 'User'}`}
          >
            <span className="sr-only">Signed in as </span>
            {authUser?.display_name || 'User'}
          </span>
          <button
            onClick={toggleDarkMode}
            className="relative p-2 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            aria-label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
        <Outlet />

        {/* Legal footer */}
        <footer className="hidden lg:block border-t border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-xs text-gray-400 dark:text-gray-500">
          <NavLink to="/privacy-policy" className="hover:underline">Privacy Policy</NavLink>
          {' · '}
          <NavLink to="/terms-of-service" className="hover:underline">Terms of Service</NavLink>
          {' · '}
          <a href="mailto:support@crewforge.co.uk" className="hover:underline">Contact</a>
        </footer>
      </main>

      {/* Mobile Bottom Navigation - hidden on desktop */}
      {(() => {
        // Helper to render a single nav item (used both inline and inside the More overflow sheet)
        const renderMobileNavItem = (item, onClickExtra) => (
          <NavLink
            key={item.path}
            to={item.path}
            end
            className={navLinkClasses}
            onClick={onClickExtra}
          >
            <div className="relative">
              <item.icon />
              {item.path === '/department/orders' && adminNotifCount > 0 && (
                <span className={`absolute -top-2 -right-2 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] shadow-lg ${
                  deptOrdersBadgeColour === 'red' ? 'bg-red-500' : (deptOrdersBadgeColour === 'yellow' || deptOrdersBadgeColour === 'amber') ? 'bg-yellow-500' : 'bg-green-500'
                }`} data-testid="admin-notif-badge-mobile" title={`${adminUrgency.red} urgent, ${adminUrgency.yellow} moderate, ${adminUrgency.green} low priority`}>
                  {adminNotifCount > 99 ? '99+' : adminNotifCount}
                </span>
              )}
              {item.path === '/department/orders' && adminNotifCount === 0 && (urgencySummary.deptOrders.color === 'red' || urgencySummary.deptOrders.color === 'amber') && (
                <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${urgencySummary.deptOrders.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="dept-orders-urgency-dot-mobile" title={tightLeadTimesTooltip(urgencySummary.deptOrders.count)} />
              )}
              {item.path === '/approvals' && pendingApprovalCount > 0 && (
                <span className={`absolute -top-2 -right-2 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] shadow-lg ${
                  approvalUrgency.color === 'red' ? 'bg-red-500 dark:bg-red-600' : approvalUrgency.color === 'amber' ? 'bg-amber-500 dark:bg-amber-600' : 'bg-green-500 dark:bg-green-600'
                }`} data-testid="approval-badge-mobile" title={`${approvalUrgency.red} critical, ${approvalUrgency.yellow} moderate`}>
                  {pendingApprovalCount > 99 ? '99+' : pendingApprovalCount}
                </span>
              )}
              {item.path === '/orders' && (urgencySummary.myOrders.color === 'red' || urgencySummary.myOrders.color === 'amber') && (
                <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${urgencySummary.myOrders.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="my-orders-urgency-dot-mobile" title={tightLeadTimesTooltip(urgencySummary.myOrders.count)} />
              )}
              {item.path === '/hires' && (hireUrgency.myHires.color === 'red' || hireUrgency.myHires.color === 'amber') && (
                <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${hireUrgency.myHires.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="my-hires-urgency-dot-mobile" title={`${hireUrgency.myHires.count} hire${hireUrgency.myHires.count !== 1 ? 's' : ''} with near return dates`} />
              )}
              {(item.path === '/department/hires' || item.path === '/admin/hires') && (hireUrgency.deptHires.color === 'red' || hireUrgency.deptHires.color === 'amber') && (
                <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${hireUrgency.deptHires.color === 'red' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-600 dark:bg-amber-400'}`} data-testid="dept-hires-urgency-dot-mobile" title={`${hireUrgency.deptHires.count} hire${hireUrgency.deptHires.count !== 1 ? 's' : ''} with near return dates`} />
              )}
            </div>
            <span className={`mt-0.5 max-w-[58px] sm:max-w-[72px] text-center ${MOBILE_TWO_LINE_LABELS.has(item.label) ? 'whitespace-normal line-clamp-2' : 'truncate'}`}>{mobileLabels[item.label] || item.label}</span>
          </NavLink>
        );

        const MAX_INLINE = 5; // Show up to 5 items inline (4 + More button when overflowing)
        const hasOverflow = allNavItems.length > MAX_INLINE || !!authUser; // Always show More when logged in (for sign-out access)
        const inlineItems = hasOverflow ? allNavItems.slice(0, MAX_INLINE - 1) : allNavItems;
        const overflowItems = hasOverflow ? allNavItems.slice(MAX_INLINE - 1) : [];

        return (
          <>
            <nav
              className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-[#1e3a5f] border-t border-gray-200 dark:border-transparent shadow-[0_-2px_10px_rgba(0,0,0,0.08)] dark:shadow-[0_-2px_10px_rgba(0,0,0,0.15)] z-50"
              aria-label="Mobile navigation"
            >
              <div className="flex justify-around items-center h-16">
                {inlineItems.map((item) => renderMobileNavItem(item))}
                {hasOverflow && (
                  <button
                    type="button"
                    onClick={() => setMoreMenuOpen((o) => !o)}
                    aria-label="More navigation items"
                    aria-expanded={moreMenuOpen}
                    data-testid="bottom-nav-more"
                    className={`flex flex-col items-center justify-center px-0.5 sm:px-1 py-1 text-[9px] sm:text-[10px] leading-tight transition-colors duration-150 min-w-0 ${
                      moreMenuOpen
                        ? 'text-indigo-700 dark:text-white font-semibold'
                        : 'text-gray-500 hover:text-gray-700 dark:text-blue-200 dark:hover:text-white'
                    }`}
                  >
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="5" cy="12" r="1.5" fill="currentColor" />
                        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                        <circle cx="19" cy="12" r="1.5" fill="currentColor" />
                      </svg>
                    </div>
                    <span className="mt-0.5 truncate max-w-[58px] sm:max-w-[72px] text-center">More</span>
                  </button>
                )}
              </div>
            </nav>

            {/* More overflow sheet */}
            {hasOverflow && moreMenuOpen && (
              <>
                <div
                  className="lg:hidden fixed inset-0 bg-black/40 z-40"
                  onClick={() => setMoreMenuOpen(false)}
                  aria-hidden="true"
                />
                <div
                  className="lg:hidden fixed bottom-16 left-0 right-0 bg-white dark:bg-[#1e3a5f] border-t border-gray-200 dark:border-transparent shadow-[0_-4px_12px_rgba(0,0,0,0.2)] z-50 max-h-[60vh] overflow-y-auto"
                  role="menu"
                  aria-label="Additional navigation"
                  data-testid="bottom-nav-more-sheet"
                >
                  <div className="grid grid-cols-4 gap-1 p-3">
                    {overflowItems.map((item) => renderMobileNavItem(item, () => setMoreMenuOpen(false)))}
                    <button
                      onClick={async () => { setMoreMenuOpen(false); await logout(); window.location.href = '/login'; }}
                      className="flex flex-col items-center justify-center px-0.5 sm:px-1 py-1 text-[9px] sm:text-[10px] leading-tight transition-colors duration-150 min-w-0 text-gray-500 hover:text-gray-700 dark:text-blue-200 dark:hover:text-white"
                      data-testid="mobile-logout"
                    >
                      <div className="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <span className="mt-0.5 truncate max-w-[58px] sm:max-w-[72px] text-center">Sign out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}
    </div>
    </NotificationFeedContext.Provider>
  );
}

export default Layout;
